import { profileSummaryForAssistant, applyAssistantProfileUpdate, mergePersonalization } from '../personalization';
import { defineTool, type CartAssistantComponent, type CartAssistantTool } from "./types";
import { liveState, useStore } from "../store";
import { getFormattedStoreDisplayName, sanitizeStoreCoordinates, battleStoreStreets } from "../../../utils/storeUtils";
import { z } from "zod";
import { SupabaseClient } from "@supabase/supabase-js";
import {
  getHistory,
  getHistoryEntry,
  getProfile,
  getCategories as getCategoriesFromDB,
  getMasterInsight,
  deleteHistoryEntry,
  updateProfile,
  getGlobalLatticePrices,
  getBatchGlobalLatticePrices
} from "../../../services/supabaseService";
import { 
  suggestCategory,
  getNearbyStores,
  searchStores,
  runMerchantBattle,
  extractItemsFromImage,
  getProductDictionary,
  geocodeStore,
  classifyExtractionError
} from "../../../services/geminiService";
import { 
  ShoppingItemSchema, 
  AiProfileSchema, 
  AiProfileBaseSchema,
  CategoryInfoSchema,
  StoreInfoSchema,
  ShoppingHistoryEntrySchema
} from "../schemas";
import { getProductSignatureRuleSummary } from "../../../services/promptConstants";
import {
  formatDisplayName,
  constructProtocolSlug,
  readShelfUnitPrice,
  compareUnitPrice
} from "../intelligence";
import { getUnitPriceCheck, describeUnitPriceCheck, unitPriceCheckNote, sizeFixUpdates } from "../basketItem";
import {
  MarketDiscoveryUnavailableError,
  discoverProducts,
  resolveMarketDeals,
  resolveMarketProductForBasket,
  searchLatticeByName
} from "../marketDiscovery";
import {
  mergeStorePriceSources,
  computeStoreTotalsAndSplit,
  enrichSmartSplitWithRoute,
  resolveUncoveredItemsLive,
  CHAT_LIVE_DISCOVERY_CAP,
  type BasketOptimizationItem,
  type MergeSourcesInput
} from "../basketOptimization";

// Import Generative UI Components
import { CurrentBasketDisplay } from "../../components/cartAssistant/generative/CurrentBasketDisplay";
import { DetailedShoppingTripCard } from "../../components/cartAssistant/generative/DetailedShoppingTripCard";
import { ConfirmActionCard } from "../../components/cartAssistant/generative/ConfirmActionCard";
import { SpendingByCategoryPieChart } from "../../components/cartAssistant/generative/SpendingByCategoryPieChart";
import { SpendingOverTimeLineChart } from "../../components/cartAssistant/generative/SpendingOverTimeLineChart";
import { HistoryListCard } from "../../components/cartAssistant/generative/HistoryListCard";
import { StoreComparisonBarChart } from "../../components/cartAssistant/generative/StoreComparisonBarChart";
import { ItemPriceHistoryLineChart } from "../../components/cartAssistant/generative/ItemPriceHistoryLineChart";
import { StoreLocationCard } from "../../components/cartAssistant/generative/StoreLocationCard";
import { DisambiguationCard } from "../../components/cartAssistant/generative/DisambiguationCard";
import { MerchantMapCard } from "../../components/cartAssistant/generative/MerchantMapCard";
import { UserProfileCard } from "../../components/cartAssistant/generative/UserProfileCard";
import { StoreBattleCard } from "../../components/cartAssistant/generative/StoreBattleCard";
import { BasketOptimizationCard } from "../../components/cartAssistant/generative/BasketOptimizationCard";
import { ReceiptMetadataCard } from "../../components/cartAssistant/generative/ReceiptMetadataCard";
import { LiveMarketDiscoveryCard } from "../../components/cartAssistant/generative/LiveMarketDiscoveryCard";
import { LocalOffersCard } from "../../components/cartAssistant/generative/LocalOffersCard";
import { fetchItemPriceHistory, fetchSpendingAnalytics } from "../cachedQueries";
import { CALIBRATION_DEFAULTS } from '../calibration';

/**
 * Components Array - A collection of CartAssistant components to register
 */

/**
 * A chat image as the basket scan sends it: chat attachments are stored as the
 * original photo (often several MB), which /api/ai/proxy and Vercel's 4.5 MB
 * request limit refuse, so it's resized like App.tsx's scans (1200 px wide).
 */
const scanImageBase64 = async (blob: Blob): Promise<string> => {
  const { processImage } = await import("../../../services/imageUtils");
  return (await processImage(blob, 1200)).base64;
};

export const components: CartAssistantComponent[] = [
  {
    name: "UserProfileCard",
    component: UserProfileCard,
    description: "Displays the user's profile information including their AI sidekick identity and knowledge domains. Use this when the user asks about their profile, persona, or settings.",
    propsSchema: z.any(),
  },
  {
    name: "BasketOptimizationCard",
    component: BasketOptimizationCard,
    description: "Displays the results of a Basket IQ optimization analysis, showing the Smart Split route and the Best Single Store leaderboard. Use this after calling 'optimize_basket' tool.",
    propsSchema: z.object({
      basketItemCount: z.number().optional(),
      bestSingleStore: z.object({
        storeName: z.string(),
        total: z.number(),
        matches: z.number(),
        coverage: z.number(),
      }).nullable().optional(),
      smartSplit: z.object({
        total: z.number(),
        route: z.array(z.object({
          name: z.string(),
          store: z.string(),
          price: z.number(),
          tier: z.enum(['verified', 'estimate']).optional().describe("'verified' = Lattice/personal history, 'estimate' = Live Market Discovery"),
        })),
        routeStats: z.object({
          totalDistanceMiles: z.number().optional(),
          totalDurationMinutes: z.number().optional(),
          extraMinutesVsSingleStore: z.number().optional(),
          tripCostDollars: z.number().optional().describe("Distance + time cost of the route in dollars, per Basket IQ's route cost model (IRS mileage rate + the user's value-of-time setting) — added on top of item price for the true total."),
        }).nullable().optional().describe("Real Mapbox driving distance/time for this route, when coordinates were available — text-only, no map in chat."),
      }).nullable().optional(),
      otherOptions: z.array(z.object({
        storeName: z.string(),
        total: z.number(),
        matches: z.number(),
        coverage: z.number(),
      })).optional(),
      currencySymbol: z.string().optional(),
    }),
  },
  {
    name: "StoreBattleCard",
    component: StoreBattleCard,
    description: "Displays a detailed side-by-side price comparison (Scoreboard) between two stores. Use this after calling 'compare_stores' tool. Pass storeAAddresses and storeBAddresses from the tool result unchanged so each location is visible below the store name. Never invent addresses. Pass totalA, totalB, sizeAdjusted, excludedCount and the commonItems size-comparison fields from compare_stores unchanged; totals are already size-adjusted.",
    propsSchema: z.object({
      storeA: z.string().optional(),
      storeB: z.string().optional(),
      storeAAddresses: z.array(z.string()).optional(),
      storeBAddresses: z.array(z.string()).optional(),
      winner: z.string().optional(),
      savings: z.number().optional(),
      itemCount: z.number().optional(),
      totalA: z.number().optional(),
      totalB: z.number().optional(),
      sizeAdjusted: z.boolean().optional(),
      excludedCount: z.number().optional(),
      commonItems: z.array(z.object({
        canonicalName: z.string(),
        priceA: z.number(),
        priceB: z.number(),
        priceAAtSizeB: z.number().nullable().optional(),
        sizeComparable: z.boolean().optional(),
        normalizedRatio: z.number().optional(),
        unitAnalysis: z.object({
          isSameSize: z.boolean(),
          normalizedRatio: z.number(),
          unitMessage: z.string(),
        }).optional(),
      })).optional(),
      currencySymbol: z.string().optional(),
      shoppedAt: z.string().optional().describe("The store the user actually bought these items at (one of storeA/storeB), when they're asking about a past trip. If it isn't the winner, the card shows how much they overpaid instead of the winner's advantage."),
    }),
  },
  {
    name: "MerchantMapCard",
    component: MerchantMapCard,
    description: "Displays one or more retail locations on an interactive map. Use this when the user asks to find nearby stores, see locations, or plot their shopping history or spending on a map (pass totalCost per store to show spend).",
    propsSchema: z.object({
      stores: z.array(z.object({
        name: z.string().optional(),
        address: z.string().optional(),
        url: z.string().optional(),
        coordinates: z.object({
          lat: z.number(),
          lng: z.number(),
        }).optional(),
        totalCost: z.number().optional().describe("Amount the user spent at this store, shown on the marker"),
      })).optional().describe("Array of stores to plot on the map"),
      title: z.string().optional().describe("Title for the map card"),
    }),
  },
  {
    name: "HistoryListCard",
    component: HistoryListCard,
    description: "Displays a summarized list of recent shopping trips. Use this when the user asks to see, list, or browse their shopping history.",
    propsSchema: z.object({
      trips: z.array(z.object({
        id: z.string().optional(),
        date: z.string().optional(),
        totalCost: z.number().optional(),
        storeName: z.string().optional(),
        itemCount: z.number().optional(),
      })).optional().describe("Array of summarized shopping trips"),
      currencySymbol: z.string().optional().describe("Currency symbol"),
    }),
  },
  {
    name: "StoreComparisonBarChart",
    component: StoreComparisonBarChart,
    description: "Compares an additive money total between stores (the card shows each store's bar plus the sum). For item spending use the stores from get_item_spending_by_store with metric = totalSpend. NEVER pass unit prices here — those belong in ItemPriceHistoryLineChart.",
    propsSchema: z.object({
      data: z.array(z.object({
        storeName: z.string().optional(),
        metric: z.number().optional(),
      })).optional().describe("Array of store metrics for comparison"),
      title: z.string().optional().describe("Chart title"),
      metricLabel: z.string().optional().describe("Label for the metric (e.g., 'Total Spend')"),
      currencySymbol: z.string().optional(),
    }),
  },
  {
    name: "ItemPriceHistoryLineChart",
    component: ItemPriceHistoryLineChart,
    description: "Displays the price history of a single item over time across different purchases. Use this when the user asks about the price trend of a specific item.",
    propsSchema: z.object({
      itemName: z.string().optional().describe("Name of the item"),
      data: z.array(z.object({
        date: z.string().optional(),
        price: z.number().optional(),
        store: z.string().optional(),
      })).optional().describe("Historical price data points"),
      currencySymbol: z.string().optional(),
    }),
  },
  {
    name: "StoreLocationCard",
    component: StoreLocationCard,
    description: "Displays details about a specific store location found during search or discovery.",
    propsSchema: z.object({
      store: z.object({
        name: z.string().optional(),
        address: z.string().optional(),
        url: z.string().optional(),
        coordinates: z.object({
          lat: z.number(),
          lng: z.number(),
        }).optional(),
      }).optional().describe("Store location details"),
    }),
  },
  {
    name: "DisambiguationCard",
    component: DisambiguationCard,
    description: "Presents multiple options to the user when a request is ambiguous (e.g., multiple items or stores match).",
    propsSchema: z.object({
      question: z.string().optional().describe("The clarification question to ask the user"),
      options: z.array(z.string()).optional().describe("List of options for the user to choose from"),
      type: z.enum(["item", "store"]).optional().describe("Type of entities being disambiguated"),
    }),
  },
  {
    name: "CurrentBasketDisplay",
    component: CurrentBasketDisplay,
    description: "Displays the items currently in the user's shopping basket.",
    propsSchema: z.object({
      items: z.array(z.object({
        id: z.string().optional(),
        name: z.string().optional(),
        price: z.number().optional(),
        quantity: z.number().optional(),
        category: z.string().optional(),
        imageUrl: z.string().optional(),
        brand: z.string().optional().nullable(),
        product: z.string().optional().nullable(),
        variety: z.string().optional().nullable(),
        size: z.number().optional().nullable(),
        unit: z.string().optional().nullable(),
      })).optional().describe("Array of shopping items with ALL fields including imageUrl"),
      currencySymbol: z.string().optional().describe("Currency symbol to display"),
    }),
  },
  {
    name: "DetailedShoppingTripCard",
    component: DetailedShoppingTripCard,
    description: "Displays a detailed view of a historical shopping trip.",
    propsSchema: z.object({
      entry: z.object({
        id: z.string().optional(),
        date: z.string().optional(),
        totalCost: z.number().optional(),
        items: z.array(z.object({
          name: z.string().optional(),
          price: z.number().optional(),
          quantity: z.number().optional(),
          category: z.string().optional(),
          imageUrl: z.string().optional(),
        })).optional(),
        storeInfo: z.object({
          name: z.string().optional(),
          address: z.string().optional(),
          url: z.string().optional(),
        }).optional(),
      }).optional().describe("The shopping history entry object"),
      currencySymbol: z.string().optional().describe("Currency symbol to display"),
    }),
  },
  {
    name: "ConfirmActionCard",
    component: ConfirmActionCard,
    description: "A card to ask for user confirmation before performing an action.",
    propsSchema: z.object({
      message: z.string().optional().describe("The confirmation message to show the user"),
      title: z.string().optional().describe("Card title"),
      confirmLabel: z.string().optional().describe("Label for the green confirm button"),
      cancelLabel: z.string().optional().describe("Label for the red cancel button"),
      variant: z.enum(["danger", "info", "success"]).optional().describe("Visual variant"),
      action: z.string().optional().describe("The internal identifier of the action being confirmed"),
      nonce: z.string().describe("The unique identifier from request_confirmation tool output"),
      items: z.array(z.object({
        name: z.string(),
        price: z.number().optional(),
        quantity: z.number().optional(),
        imageUrl: z.string().optional(),
        brand: z.string().optional().nullable(),
        product: z.string().optional().nullable(),
        variety: z.string().optional().nullable(),
        size: z.number().optional().nullable(),
        unit: z.string().optional().nullable(),
      })).optional().describe("Optional list of items for batch confirmations"),
      currencySymbol: z.string().optional().describe("Currency symbol to display (e.g. $, £)"),
    }),
  },
  {
    name: "SpendingOverTimeLineChart",
    component: SpendingOverTimeLineChart,
    description: "A line chart visualizing spending trends over time. Use this when the user asks for spending history trends, graphs, or changes over time. Get the data from get_spending_over_time (pass its buckets as data).",
    propsSchema: z.object({
      data: z.array(z.object({
        dateLabel: z.string().optional(),
        totalSpend: z.number().optional(),
        tripCount: z.number().optional().describe("Number of trips in this period"),
        topCategory: z.string().optional().describe("Top spending category in this period"),
        primaryStore: z.string().optional().describe("Most-visited store in this period"),
      })).optional().describe("Historical spending data points"),
      title: z.string().optional().describe("Chart title"),
      currencySymbol: z.string().optional().describe("Currency symbol"),
    }),
  },
  {
    name: "SpendingByCategoryPieChart",
    component: SpendingByCategoryPieChart,
    description: "A pie chart visualizing spending breakdown by category. Use this when the user asks for a breakdown, percentages, or category-wise analysis. Get the data from get_spending_by_category (pass its categories as data). Expects an array of { categoryName: string, amount: number, color?: string } — color is optional; omit it to use the CartLens palette.",
    propsSchema: z.object({
      data: z.array(z.object({
        categoryName: z.string().optional(),
        amount: z.number().optional(),
        color: z.string().optional(),
      })).optional().describe("Category spending data"),
      title: z.string().optional().describe("Chart title"),
      currencySymbol: z.string().optional().describe("Currency symbol"),
    }),
  },
  {
    name: "ReceiptMetadataCard",
    component: ReceiptMetadataCard,
    description: "Shows the detected store location and trip date/time from a receipt scan, with an 'Extract Items' button the user must click to proceed. Render this immediately after calling set_receipt_metadata.",
    propsSchema: z.object({
      storeName: z.string().describe("Store name"),
      storeAddress: z.string().optional().describe("Store address"),
      date: z.string().optional().describe("Trip date (YYYY-MM-DD)"),
      time: z.string().optional().describe("Trip time (HH:mm)"),
      nonce: z.string().describe("Unique nonce from set_receipt_metadata"),
      itemCount: z.number().optional().describe("Number of items detected"),
    }),
  },
  {
    name: "LiveMarketDiscoveryCard",
    component: LiveMarketDiscoveryCard,
    description: "Displays Live Market Discovery results: distinct products matching a free-text search, each with a list of local store prices. Use this after calling 'discover_live_market_products'.",
    propsSchema: z.object({
      query: z.string().optional().describe("The search query that produced these results"),
      products: z.array(z.object({
        name: z.string(),
        brand: z.string().nullable().optional(),
        category: z.string().optional(),
        size: z.union([z.number(), z.string()]).nullable().optional(),
        unit: z.string().nullable().optional(),
        imageUrl: z.string().nullable().optional(),
        upc: z.string().nullable().optional(),
        localPrices: z.array(z.object({
          storeName: z.string(),
          price: z.number(),
          distance: z.number().nullable().optional(),
          storeLogoUrl: z.string().nullable().optional(),
          pricePerUnit: z.number().nullable().optional(),
        })),
      })).optional().describe("Array of discovered products, from discover_live_market_products"),
      currencySymbol: z.string().optional(),
    }),
  },
  {
    name: "LocalOffersCard",
    component: LocalOffersCard,
    description: "Displays 'Local Offers' — nearby and regional store prices — for one specific basket item. Use this after calling 'get_local_offers_for_item'.",
    propsSchema: z.object({
      itemName: z.string().optional(),
      itemPrice: z.number().optional().describe("The basket item's own price, used to show a save/overpay badge per offer"),
      userLocationAddress: z.string().optional(),
      deals: z.array(z.object({
        name: z.string(),
        productName: z.string().optional(),
        storeName: z.string(),
        storeAddress: z.string().optional(),
        price: z.number(),
        distance: z.number().nullable().optional(),
        storeLogoUrl: z.string().nullable().optional(),
        pricePerUnit: z.number().nullable().optional(),
        unit: z.string().nullable().optional(),
        imageUrl: z.string().nullable().optional(),
        sourceUrl: z.string().nullable().optional(),
      })).optional().describe("Array of local offers, from get_local_offers_for_item"),
      currencySymbol: z.string().optional(),
    }),
  },
];

/**
 * createTools - Function to create CartAssistant tools with injected dependencies.
 * 
 * CRITICAL ARCHITECTURE: To avoid stale closures and tool interruption during 
 * state updates, this function should be called with STABLE references.
 * Inside tool handlers, we prefer using liveState or useStore.getState() 
 * to access the most current application state and setters.
 */
export const createTools = (
  getSupabase: () => SupabaseClient | null, 
  getUserId: () => string | undefined,
  language: string = 'en',
  unitSystem: 'imperial' | 'metric' = 'imperial',
  massUnits: string[] = ['oz', 'lb'],
  currencySymbol: string = '$',
  t: any,
  isPro: boolean = false,
  isUltra: boolean = false
): CartAssistantTool[] => {
  /**
   * Maps a Gemini API error to an AI-instruction string the assistant can relay to the user.
   */
  const getExtractionErrorMessage = (err: any): string => {
    const type = classifyExtractionError(err);
    if (type === 'service_unavailable') {
      return "The image recognition service (Gemini) is temporarily unavailable due to high demand (503). Tell the user: the AI vision service is overloaded right now — please wait a minute and try again. Do NOT ask them to retake or re-upload the photo.";
    }
    if (type === 'rate_limit') {
      return "The image recognition service hit its rate limit (429). Tell the user: the service is temporarily rate-limited — please wait 30–60 seconds and try again.";
    }
    return "Extraction failed due to an unexpected error. Tell the user we couldn't read the image and ask them to try again. Suggest retaking it with better lighting or a closer angle if the issue persists.";
  };

  const getContext = () => {
    const supabase = getSupabase();
    const userId = getUserId();
    if (!supabase || !userId) throw new Error(t('chat.upload_error_auth'));
    return { supabase, userId };
  };

  /**
   * Every recorded purchase of an item, one row per line item with its own store.
   * Matches on the item name and on any canonical product whose name matches, so
   * OCR variants of the same product are all included. Search is singular-tolerant
   * ("peanuts" also matches "Peanut Butter Crackers"'s "Peanut").
   */
  const fetchItemPurchases = async (itemName: string) => {
    const { supabase, userId } = getContext();
    const term = itemName.trim().replace(/s$/i, itemName.trim().length > 4 ? '' : 's');
    const { data: canonicals } = await supabase
      .from('product_canonical')
      .select('id')
      .ilike('name', `%${term}%`)
      .limit(25);
    const canonicalIds = (canonicals || []).map((c: any) => c.id);

    // Only the user's own trips, not receipts saved as "Not my trip".
    const select = 'id, name, price, quantity, history:shopping_history!inner(purchase_date, is_personal_trip, stores(name, address, latitude, longitude))';
    const queries = [
      supabase.from('shopping_items').select(select).eq('user_id', userId).eq('history.is_personal_trip', true).ilike('name', `%${term}%`),
      ...(canonicalIds.length ? [supabase.from('shopping_items').select(select).eq('user_id', userId).eq('history.is_personal_trip', true).in('canonical_id', canonicalIds)] : []),
    ];
    const results = await Promise.all(queries);
    const rows = new Map<string, any>();
    for (const { data, error } of results) {
      if (error) throw error;
      for (const row of data || []) rows.set(row.id, row);
    }

    const toLocalDate = (value: string) => {
      const d = new Date(value);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    return Array.from(rows.values())
      .filter((r) => r.history?.purchase_date && Number.isFinite(Number(r.price)))
      .map((r) => ({
        name: r.name as string,
        unitPrice: Number(r.price),
        quantity: Number(r.quantity) > 0 ? Number(r.quantity) : 1,
        date: toLocalDate(r.history.purchase_date),
        store: (r.history.stores as any)?.name || 'Unknown Store',
        storeAddress: (r.history.stores as any)?.address || undefined,
        storeCoordinates: sanitizeStoreCoordinates({ lat: (r.history.stores as any)?.latitude, lng: (r.history.stores as any)?.longitude }),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  return [
    defineTool({
      name: "trigger_visual_action",
      description: "Triggers a specific UI action like opening a digital receipt or original photo. REQUIRED for satisfying user requests to 'show the receipt' or 'view original photo'.",
      inputSchema: z.object({
        action: z.enum(["VIEW_DIGITAL_RECEIPT", "VIEW_ORIGINAL_RECEIPT"]).describe("The visual action to perform"),
        entryId: z.string().describe("The history entry ID the action applies to"),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string() }),
      tool: async ({ action, entryId }) => {
        const { supabase, userId } = getContext();
        const entry = await getHistoryEntry(supabase, userId, entryId);
        if (!entry) return { success: false, message: "Trip not found." };

        if (liveState.triggerAction) {
          liveState.triggerAction(action, entry);
          return { success: true, message: `Action ${action} triggered for trip at ${entry.storeInfo?.name}.` };
        }
        
        return { success: false, message: "UI bridge not initialized." };
      }
    }),

    defineTool({
      name: "get_user_location",
      description: "Gets the user's real-time GPS coordinates from their device and silently updates the active store to the nearest known location in the background. ALWAYS call this before any 'near me' or 'cheapest nearby' lattice or store query — even if a store is already selected, the user may have physically moved. Returns only lat/lng for passing to search_global_lattice or find_nearby_stores. Do NOT treat this as a substitute for find_nearby_stores — always call find_nearby_stores separately when the user wants a list of nearby stores.",
      inputSchema: z.object({}),
      outputSchema: z.object({
        success: z.boolean(),
        lat: z.number().optional(),
        lng: z.number().optional(),
        accuracy: z.number().optional(),
        address: z.string().optional(),
        message: z.string()
      }),
      tool: async () => {
        // Step 1: Get GPS coordinates
        const position = await new Promise<GeolocationPosition | null>((resolve) => {
          if (typeof navigator === 'undefined' || !navigator.geolocation) {
            resolve(null);
            return;
          }
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
          );
        });

        if (!position) {
          // GPS blocked or unavailable: fall back to the active store, then the profile ZIP,
          // so "near me" queries still work. Neither path changes the active store.
          const { useStore } = await import("../store");
          const { geocodeAddress } = await import("../geocoding");
          const { currentStore, zipCode } = useStore.getState();

          let storeCoords = sanitizeStoreCoordinates(currentStore?.coordinates);
          if (!storeCoords && currentStore?.address) {
            storeCoords = await geocodeAddress(`${currentStore.name}, ${currentStore.address}`);
          }
          if (storeCoords) {
            return {
              success: true,
              lat: storeCoords.lat,
              lng: storeCoords.lng,
              address: currentStore?.address,
              message: `GPS is unavailable, so this is the location of the user's current store (${currentStore?.name}${currentStore?.address ? `, ${currentStore.address}` : ''}). Use it as the user's location and pass lat/lng to search_global_lattice or find_nearby_stores. Briefly mention results are based on their current store.`
            };
          }

          const zipCoords = zipCode?.trim() ? await geocodeAddress(zipCode.trim()) : undefined;
          if (zipCoords) {
            return {
              success: true,
              lat: zipCoords.lat,
              lng: zipCoords.lng,
              address: zipCode!.trim(),
              message: `GPS is unavailable, so this is the center of the user's saved ZIP code ${zipCode}. Use it as the user's location and pass lat/lng to search_global_lattice or find_nearby_stores. Briefly mention results are based on their ZIP code.`
            };
          }

          return {
            success: false,
            message: "Could not determine the user's location: GPS is blocked, no store is selected and no ZIP code is saved. Ask the user to allow location access, select their current store, or set a ZIP code."
          };
        }

        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const accuracy = Math.round(position.coords.accuracy);

        const applyStore = (raw: any) => {
          const formatted = { ...raw, name: getFormattedStoreDisplayName(raw) };
          liveState.setCurrentStore(formatted);
        };

        // Step 2: Run reverse geocoding + nearest store lookup in parallel
        const [geoResult, storeResult] = await Promise.allSettled([
          // Reverse geocode for human-readable address (Nominatim, no API key required)
          fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=en`,
            { headers: { 'User-Agent': 'CartLens/1.0 (cartlens.co)' } }
          ).then(r => r.json()).then(d => (d.display_name as string) || null),

          // Nearest store via Gemini Maps
          getNearbyStores(lat, lng)
        ]);

        // Apply human-readable address if available
        const address = geoResult.status === 'fulfilled' && geoResult.value
          ? geoResult.value as string
          : undefined;

        // Apply nearest store — fall back to Lattice DB if Gemini Maps failed
        if (storeResult.status === 'fulfilled' && storeResult.value?.length > 0) {
          applyStore(storeResult.value[0]);
        } else {
          try {
            const supabase = getSupabase();
            if (supabase) {
              const { data: nearby } = await supabase
                .from('stores')
                .select('name, address, latitude, longitude, url')
                .not('latitude', 'is', null)
                .not('longitude', 'is', null)
                .gte('latitude', lat - 0.5)
                .lte('latitude', lat + 0.5)
                .gte('longitude', lng - 0.5)
                .lte('longitude', lng + 0.5)
                .limit(50);

              if (nearby && nearby.length > 0) {
                const closest = nearby.reduce((best: any, s: any) => {
                  const dS = (s.latitude - lat) ** 2 + (s.longitude - lng) ** 2;
                  const dB = (best.latitude - lat) ** 2 + (best.longitude - lng) ** 2;
                  return dS < dB ? s : best;
                });
                applyStore({
                  name: closest.name,
                  address: closest.address,
                  coordinates: { lat: closest.latitude, lng: closest.longitude },
                  url: closest.url,
                });
              }
            }
          } catch (dbErr) {
            console.warn("[CartAssistant] get_user_location: fallback store lookup failed:", dbErr);
          }
        }

        const locationLabel = address || `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
        return {
          success: true,
          lat,
          lng,
          accuracy,
          address,
          message: `You are currently at ${locationLabel} (±${accuracy}m accuracy). Pass lat/lng to search_global_lattice or find_nearby_stores.`
        };
      }
    }),

    defineTool({
      name: "get_lattice_insights",
      description: "Checks the Global Lattice for better prices on a specific item when you already have its canonicalId. For 'find cheapest X near me' queries without a canonicalId, use search_global_lattice instead. Call get_user_location first to pass real-time coordinates.",
      inputSchema: z.object({
        canonicalId: z.string().describe("The semantic English slug of the product (e.g. heinz-tomato-ketchup-20oz)"),
        lat: z.number().optional().describe("User's real-time latitude from get_user_location"),
        lng: z.number().optional().describe("User's real-time longitude from get_user_location"),
        upc: z.string().optional().describe("Verified 12-digit UPC"),
        gtin: z.string().optional().describe("Verified 14-digit GTIN"),
        pluCode: z.string().optional().describe("Verified PLU code for produce"),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        bestPrice: z.number().optional(),
        storeName: z.string().optional(),
        storeAddress: z.string().optional(),
        distanceMiles: z.number().optional(),
        message: z.string()
      }),
      tool: async ({ canonicalId, lat, lng, pluCode, upc, gtin }) => {
        const { supabase, userId } = getContext();

        try {
          const profile = await getProfile(supabase, userId);
          const radius = profile?.settings?.intelligence?.lattice_radius_miles || (isUltra ? CALIBRATION_DEFAULTS.latticeRadiusMiles : 3);
          const horizon = profile?.settings?.intelligence?.lattice_horizon_hours || CALIBRATION_DEFAULTS.latticeHorizonHours;

          const prices = await getGlobalLatticePrices(
            supabase,
            canonicalId,
            lat,
            lng,
            radius,
            horizon,
            null,
            undefined,
            pluCode,
            upc,
            gtin
          );

          if (prices && prices.length > 0) {
            const best = prices[0];
            const distanceNote = best.distance_miles != null ? ` (${Number(best.distance_miles).toFixed(1)} mi away)` : '';
            return {
              success: true,
              bestPrice: best.price,
              storeName: best.store_name,
              storeAddress: best.store_address || undefined,
              distanceMiles: best.distance_miles != null ? Number(best.distance_miles) : undefined,
              message: `Lattice Intel: ${currencySymbol}${best.price} at ${best.store_name}${distanceNote}.`
            };
          }
          return { success: false, message: `No prices found in the Lattice within ${radius} miles in the last ${horizon} hours.` };
        } catch (err) {
          return { success: false, message: "Lattice discovery failed." };
        }
      }
    }),

    defineTool({
      name: "search_global_lattice",
      description: "Searches the Global Lattice for the cheapest prices of a product by name across all nearby stores. ALWAYS call 'get_user_location' first and pass lat/lng here so results are anchored to where the user physically is right now. Respects Intelligence Calibration (radius + horizon). Returns store addresses and coordinates so results can be shown on a MerchantMapCard.",
      inputSchema: z.object({
        productName: z.string().describe("The product name to search for (e.g. 'Vital Farms Eggs', 'pizza', 'Tide detergent')"),
        lat: z.number().optional().describe("User's real-time latitude from get_user_location — required for 'near me' accuracy"),
        lng: z.number().optional().describe("User's real-time longitude from get_user_location — required for 'near me' accuracy"),
        radiusMiles: z.number().optional().describe("Override radius in miles (leave empty to use Intelligence Calibration setting)"),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        results: z.array(z.object({
          productName: z.string(),
          price: z.number(),
          storeName: z.string(),
          storeAddress: z.string().optional(),
          lat: z.number().optional(),
          lng: z.number().optional(),
          distanceMiles: z.number().optional(),
          date: z.string(),
          size: z.union([z.number(), z.string()]).optional(),
          unit: z.string().optional(),
          pricePerUnit: z.number().optional().describe("Price divided by size, in the given unit — use this over 'price' to say which result is actually the better value, since pack sizes can differ"),
        })),
        message: z.string()
      }),
      tool: async ({ productName, lat, lng, radiusMiles }) => {
        try {
          const { supabase, userId } = getContext();

          // Intelligence Calibration: use profile settings, fall back to tier defaults
          const profile = await getProfile(supabase, userId);
          const radius = radiusMiles ?? profile?.settings?.intelligence?.lattice_radius_miles ?? (isUltra ? CALIBRATION_DEFAULTS.latticeRadiusMiles : 3);
          const horizon = profile?.settings?.intelligence?.lattice_horizon_hours ?? CALIBRATION_DEFAULTS.latticeHorizonHours;

          const results = await searchLatticeByName(supabase, productName, { lat, lng, radiusMiles: radius, lookbackHours: horizon });

          if (results.length === 0) {
            const radiusNote = lat && lng ? ` within ${radius} miles of your location` : '';
            return {
              success: false,
              results: [],
              message: `No price data found for "${productName}"${radiusNote} in the last ${horizon} hours.`
            };
          }

          const radiusNote = lat && lng ? ` within ${radius} miles of your location` : '';
          return {
            success: true,
            results,
            message: `Found ${results.length} price point(s) for "${productName}"${radiusNote} (last ${horizon}h). Use MerchantMapCard to display store locations.`
          };
        } catch (err) {
          console.error("[CartAssistant] search_global_lattice failed:", err);
          return { success: false, results: [], message: "Lattice search failed." };
        }
      }
    }),

    defineTool({
      name: "discover_live_market_products",
      description: "Live Market Discovery: searches nearby & regional retailers (via Serper.dev — not the Lattice) for a free-text product query, returning distinct products each with a list of local store prices. Use this when the user wants to shop for, find, or compare a product that isn't necessarily already in their basket or the Lattice — e.g. 'find the best price on milk near me' or 'search for AA batteries'. ALWAYS call get_user_location first and pass lat/lng/address here. Render results with LiveMarketDiscoveryCard; use add_market_product_to_basket afterward to add a chosen result.",
      inputSchema: z.object({
        query: z.string().describe("Product search term, e.g. 'milk', '2% Organic Milk'"),
        lat: z.number().describe("User's real-time latitude from get_user_location"),
        lng: z.number().describe("User's real-time longitude from get_user_location"),
        address: z.string().optional().describe("Human-readable address from get_user_location, for display"),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        products: z.array(z.object({
          name: z.string(),
          brand: z.string().nullable().optional(),
          productName: z.string().optional(),
          variety: z.string().nullable().optional(),
          category: z.string().optional(),
          size: z.union([z.number(), z.string()]).nullable().optional(),
          unit: z.string().nullable().optional(),
          imageUrl: z.string().nullable().optional(),
          upc: z.string().nullable().optional(),
          localPrices: z.array(z.object({
            storeName: z.string(),
            price: z.number(),
            distance: z.number().nullable().optional(),
            storeLogoUrl: z.string().nullable().optional(),
            pricePerUnit: z.number().nullable().optional(),
          })),
        })),
        message: z.string()
      }),
      tool: async ({ query, lat, lng, address }) => {
        try {
          const products = await discoverProducts({ query, userLat: lat, userLng: lng, userAddress: address });
          // Staged so add_market_product_to_basket can look up the exact product/option
          // by index without the model re-typing/hallucinating fields it already saw.
          liveState.lastMarketDiscoveryProducts = products;

          if (!products || products.length === 0) {
            return { success: false, products: [], message: `No live market products found for "${query}" near you.` };
          }
          return {
            success: true,
            products,
            message: `Found ${products.length} product(s) for "${query}" with local store prices. Render with LiveMarketDiscoveryCard. To add one, call add_market_product_to_basket with its productIndex and chosen storeName.`
          };
        } catch (err) {
          console.error("[CartAssistant] discover_live_market_products failed:", err);
          if (err instanceof MarketDiscoveryUnavailableError) {
            return {
              success: false,
              products: [],
              message: `Live Market Discovery is temporarily unavailable (the price search service is down). Tell the user live store prices can't be checked right now — do NOT say no prices exist for "${query}". Offer to try again later.`
            };
          }
          return { success: false, products: [], message: "Live market discovery failed due to an unexpected error. Tell the user to try again shortly." };
        }
      }
    }),

    defineTool({
      name: "add_market_product_to_basket",
      description: "Adds one product + store price option from the most recent discover_live_market_products results to the basket. Automatically enriches missing category/UPC/GTIN/size/unit before adding, and records a 'better deal' note if a cheaper option exists among that same product's local prices.",
      inputSchema: z.object({
        productIndex: z.number().describe("Index into the 'products' array returned by discover_live_market_products"),
        storeName: z.string().describe("The exact storeName of the chosen entry in that product's localPrices array"),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string() }),
      tool: async ({ productIndex, storeName }) => {
        const product = liveState.lastMarketDiscoveryProducts?.[productIndex];
        if (!product) {
          return { success: false, message: "No matching discovered product — call discover_live_market_products again first." };
        }
        const option = product.localPrices.find((p: any) => p.storeName === storeName);
        if (!option) {
          return { success: false, message: `No local price for "${storeName}" on this product.` };
        }

        const { useStore } = await import("../store");
        const state = useStore.getState();

        const resolved = await resolveMarketProductForBasket(product, option, state.categories.map((c: any) => c.name));

        state.addItem(
          resolved.name,
          resolved.price,
          resolved.category,
          resolved.imageUrl || undefined,
          1,
          undefined,
          resolved.unit || undefined,
          resolved.brand || undefined,
          resolved.productName || undefined,
          resolved.variety || undefined,
          resolved.size || undefined,
          undefined,
          undefined,
          product.plu || undefined,
          resolved.metadata,
          resolved.upc || undefined,
          resolved.gtin || undefined
        );

        return { success: true, message: `Added ${resolved.name} at ${storeName} (${currencySymbol}${resolved.price.toFixed(2)}) to the basket.` };
      }
    }),

    defineTool({
      name: "get_local_offers_for_item",
      description: "Gets 'Local Offers' — nearby & regional store prices — for one specific item already in the basket, the same data shown behind the info icon on a basket item. Matches by item name (case-insensitive substring) against the current basket. Reuses cached results if background grounding already ran for that item; otherwise resolves live (pass lat/lng from get_user_location in that case). Render with LocalOffersCard.",
      inputSchema: z.object({
        itemName: z.string().describe("Name (or partial name) of the basket item to find local offers for"),
        lat: z.number().optional().describe("User's real-time latitude from get_user_location — only needed if this item hasn't been grounded yet"),
        lng: z.number().optional().describe("User's real-time longitude from get_user_location — only needed if this item hasn't been grounded yet"),
        address: z.string().optional().describe("Human-readable address from get_user_location, for display"),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        itemName: z.string().optional(),
        itemPrice: z.number().optional(),
        userLocationAddress: z.string().optional(),
        deals: z.array(z.object({
          name: z.string(),
          productName: z.string().optional(),
          storeName: z.string(),
          storeAddress: z.string().optional(),
          price: z.number(),
          distance: z.number().nullable().optional(),
          storeLogoUrl: z.string().nullable().optional(),
          pricePerUnit: z.number().nullable().optional(),
          unit: z.string().nullable().optional(),
          imageUrl: z.string().nullable().optional(),
          sourceUrl: z.string().nullable().optional(),
        })),
        message: z.string()
      }),
      tool: async ({ itemName, lat, lng, address }) => {
        const { useStore } = await import("../store");
        const state = useStore.getState();
        const items = state.operatingContext === 'personnel' ? state.latticeItems : state.items;

        const item = items.find((i: any) => i.name?.toLowerCase().includes(itemName.toLowerCase()));
        if (!item) {
          return { success: false, deals: [], message: `No basket item matching "${itemName}" found.` };
        }

        let deals = item.metadata?.groundedDeals;
        let resolvedAddress = address || item.metadata?.userLocationAddress || undefined;

        if (!deals || deals.length === 0) {
          if (lat == null || lng == null) {
            return { success: false, deals: [], message: `"${item.name}" hasn't been checked for local offers yet — call get_user_location first, then retry with lat/lng.` };
          }
          const { supabase } = getContext();
          deals = await resolveMarketDeals({
            supabase,
            query: item.name,
            itemPrice: item.price,
            itemSize: item.size,
            itemUnit: item.unit,
            userLat: lat,
            userLng: lng,
            userAddress: address,
          });
          resolvedAddress = address || resolvedAddress;
          state.updateItem(item.id, {
            metadata: {
              ...(item.metadata || {}),
              groundingStatus: 'success',
              groundedDeals: deals || [],
              userLocationAddress: resolvedAddress || null
            }
          });
        }

        if (!deals || deals.length === 0) {
          return { success: true, itemName: item.name, itemPrice: item.price, deals: [], message: `No local offers found for "${item.name}" nearby.` };
        }

        return {
          success: true,
          itemName: item.name,
          itemPrice: item.price,
          userLocationAddress: resolvedAddress,
          deals,
          message: `Found ${deals.length} local offer(s) for "${item.name}". Render with LocalOffersCard.`
        };
      }
    }),

    defineTool({
      name: "process_uploaded_image",
      description: "ALWAYS call this tool FIRST when the user uploads any image — receipt OR product photo. It auto-detects the image type using the same logic as the Import button: runs Gemini extraction with receipt mode, reads the isReceipt flag from the response, and returns the correct structured data. Returns isReceipt=true for receipts (with storeName/date/items) and isReceipt=false for product/tag images (with items only). NEVER use your own vision to read an image. NEVER skip this tool and go straight to process_receipt_image or process_item_image.",
      inputSchema: z.object({
        imageUrl: z.string().describe("The URL of the uploaded image (from [img:URL] marker in the message)"),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        isReceipt: z.boolean(),
        itemCount: z.number().optional(),
        // Non-receipt confirmation payload (mirrors request_confirmation output)
        status: z.string().optional(),
        nonce: z.string().optional(),
        toolName: z.string().optional(),
        action: z.string().optional(),
        items: z.array(z.object({
          name: z.string().optional().nullable(),
          price: z.number().optional().nullable(),
          imageUrl: z.string().optional().nullable(),
          quantity: z.number().optional().nullable(),
          brand: z.string().optional().nullable(),
          product: z.string().optional().nullable(),
          variety: z.string().optional().nullable(),
          size: z.union([z.number(), z.string()]).optional().nullable(),
          unit: z.string().optional().nullable(),
          upc: z.string().optional().nullable(),
          gtin: z.string().optional().nullable(),
          plu_code: z.string().optional().nullable(),
          canonicalId: z.string().optional().nullable(),
          category: z.string().optional().nullable(),
        })).optional().nullable(),
        // Receipt-only fields
        storeName: z.string().optional().nullable(),
        storeAddress: z.string().optional().nullable(),
        date: z.string().optional().nullable(),
        time: z.string().optional().nullable(),
        subtotal: z.number().optional().nullable(),
        totalAmount: z.number().optional().nullable(),
        lat: z.number().optional().nullable(),
        lng: z.number().optional().nullable(),
        message: z.string()
      }),
      tool: async ({ imageUrl }) => {
        const { supabase, userId } = getContext();
        try {
          // SANITIZE: AI might pass the URL wrapped in [img:...] brackets from the context helper
          const cleanUrl = imageUrl.replace(/^\[img:|\]$/g, '');
          
          let blob: Blob;
          if (cleanUrl.startsWith('local:') || cleanUrl.startsWith('chat:')) {
            const { ImageCacheService } = await import("../../../services/imageCacheService");
            const localBlob = await ImageCacheService.getImageBlob(cleanUrl);
            if (!localBlob) throw new Error(`Local image not found for key: ${cleanUrl}`);
            blob = localBlob;
          } else {
            const res = await fetch(cleanUrl);
            blob = await res.blob();
          }

          const base64 = await scanImageBase64(blob);

          const categories = liveState.categories;
          const dictionary = await getProductDictionary(supabase, userId);
          const profile = await getProfile(supabase, userId);

          // Always use 'receipt' mode — identical to the Import button.
          const result = await extractItemsFromImage(
            supabase,
            base64,
            categories,
            'receipt',
            profile?.ai_profile,
            dictionary,
            unitSystem,
            massUnits,
            language,
            undefined, // No progressive addition, wait for confirmation
            false, // strict: false
            true // skipDelay: true for Chat tools
          );

          // SANITIZE: Ensure no raw nulls leak into the Zod schema from Gemini
          const sanitizeItem = (i: any) => ({
            ...i,
            name: i.name || i.product || "Unknown",
            category: i.category || "Other",
            brand: i.brand || undefined,
            product: i.product || undefined,
            variety: i.variety || undefined,
            size: i.size || undefined,
            unit: i.unit || undefined,
            upc: i.upc || undefined,
            gtin: i.gtin || undefined,
            plu_code: i.plu_code || undefined,
            canonicalId: i.canonicalId || undefined,
            price: typeof i.price === 'number' ? i.price : 0,
            quantity: typeof i.quantity === 'number' ? i.quantity : 1,
          });

          // Keep raw Gemini values (brand, product, variety, size, unit, metadata, etc.) intact for
          // staging — sanitizeItem is only for the Zod output schema, not for addItem.
          const itemsForAi = (result.items || []).map(i => {
            const { brand, product, variety, unit } = i;
            let { size } = i;
            
            // FIX: Manual Parsing for combined size/unit strings (e.g. "12oz" or "1.5lb")
            let rUnit = unit;
            if (typeof size === 'string' && !unit) {
              const m = size.match(/^([\d.]+)([a-zA-Z\s]+)$/);
              if (m) {
                size = parseFloat(m[1]);
                rUnit = m[2].trim().toLowerCase();
              } else if (!isNaN(parseFloat(size))) {
                size = parseFloat(size);
              }
            } else if (typeof size === 'string' && !isNaN(parseFloat(size))) {
              size = parseFloat(size);
            }

            const formattedName = i.name || formatDisplayName({ brand, product, variety, size, unit: rUnit });
            const protocolSlug = i.canonicalId || constructProtocolSlug({ brand, product, variety, size, unit: rUnit });
            
            return {
              ...i,
              size,
              unit: rUnit,
              name: formattedName,
              canonicalId: protocolSlug,
              embedding: i.embedding,
              imageUrl: cleanUrl,
              category: i.category || "Other",
            };
          }).filter(i => i.product || i.name);

          if (result.isReceipt) {
            // Apply metadata now (awaited) so store + date are set before the AI continues
            if (liveState.applyReceiptMetadata) {
              await liveState.applyReceiptMetadata({
                storeName: result.storeName,
                storeAddress: result.storeAddress,
                date: result.date,
                time: result.time,
                subtotal: result.subtotal,
                totalAmount: result.totalAmount,
              }).catch(err => console.warn("[CartAssistant] process_uploaded_image: applyReceiptMetadata failed:", err));
            }
            liveState.receiptMetadataSource = { storeName: result.storeName, storeAddress: result.storeAddress };
            liveState.setReceiptImageUrl(imageUrl);

            // Stage items for add_all_items_to_basket — called AFTER the user clicks "Extract Items"
            // on the ReceiptMetadataCard. Clear stale product-scan items to prevent shadowing.
            liveState.pendingReceiptItems = itemsForAi;
            liveState.pendingExtractedItems = [];

            const receiptStore = useStore.getState().currentStore;
            const resolvedLat = receiptStore?.coordinates?.lat;
            const resolvedLng = receiptStore?.coordinates?.lng;
            return {
              success: true,
              isReceipt: true,
              itemCount: itemsForAi.length,
              storeName: result.storeName || "Unknown",
              storeAddress: result.storeAddress || "",
              date: result.date || "",
              time: result.time || "",
              subtotal: result.subtotal,
              totalAmount: result.totalAmount,
              lat: resolvedLat ?? undefined,
              lng: resolvedLng ?? undefined,
              message: `Receipt scanned: ${itemsForAi.length} items found at "${result.storeName}" on ${result.date} (Subtotal: ${currencySymbol}${result.subtotal?.toFixed(2) || '?'}). MANDATORY NEXT STEP: call 'set_receipt_metadata' NOW with storeName="${result.storeName}", storeAddress="${result.storeAddress || ''}", date="${result.date}", time="${result.time}", subtotal=${result.subtotal ?? 'null'}, totalAmount=${result.totalAmount ?? 'null'}, lat=${resolvedLat ?? 'null'}, lng=${resolvedLng ?? 'null'}, itemCount=${itemsForAi.length}. Do NOT call add_all_items_to_basket yet — wait for user to click "Extract Items".${unitPriceCheckNote(itemsForAi, currencySymbol)}`
            };
          } else {
            // Product/tag image — stage full items in liveState, return only lightweight summary
            liveState.pendingExtractedItems = itemsForAi;
            if (!itemsForAi.length) {
              return { success: false, isReceipt: false, message: "Could not read item details from the image." };
            }
            const nonce = `extract_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
            // Lightweight summary for the card UI, but background payload for add_all_items_to_basket
            const cardItems = itemsForAi.map(i => ({
              name: i.name || i.product,
              category: i.category || "Other",
              price: i.price,
              imageUrl: i.imageUrl,
              quantity: i.quantity || 1,
              brand: i.brand,
              product: i.product,
              variety: i.variety,
              size: i.size,
              unit: i.unit,
              upc: i.upc,
              gtin: i.gtin,
              plu_code: i.plu_code,
              canonicalId: i.canonicalId,
            }));
            return {
              success: true,
              isReceipt: false,
              itemCount: itemsForAi.length,
              status: "AWAITING_VISUAL_CONFIRMATION_CARD",
              nonce,
              toolName: "add_all_items_to_basket",
              action: "add_extracted_items",
              items: cardItems,
              message: `Product image: ${itemsForAi.length} items extracted. MANDATORY: Render 'ConfirmActionCard' NOW with nonce="${nonce}", action="add_extracted_items", toolName="add_all_items_to_basket", message="Add ${itemsForAi.length} items to basket?", and the items array. After user confirms, call 'add_all_items_to_basket' with no arguments. DO NOT call set_receipt_metadata or any store/date metadata tools.${unitPriceCheckNote(itemsForAi, currencySymbol)}`
            };
          }
        } catch (err) {
          console.error("[CartAssistant] process_uploaded_image tool error:", err);
          return { success: false, isReceipt: false, message: getExtractionErrorMessage(err) };
        }
      }
    }),

    defineTool({
      name: "process_item_image",
      description: "Extracts structured product data for ALL items visible in a single product photo, price tag, or shelf label. Use this ONLY for non-receipt images (individual products, store shelf tags, barcode labels). CRITICAL: If the image is just a product or a price tag, use this tool. Do NOT use 'process_receipt_image' for simple product photos, even if a store name is visible. REQUIRED: call this as the FIRST action when the user uploads a product/tag image — never use your own vision to extract data.",
      inputSchema: z.object({
        imageUrl: z.string().describe("The URL of the product image (from [img:URL] marker)"),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        itemCount: z.number().optional(),
        message: z.string()
      }),
      tool: async ({ imageUrl }) => {
        const { supabase, userId } = getContext();
        try {
          let blob: Blob;
          if (imageUrl.startsWith('local:') || imageUrl.startsWith('chat:')) {
            const { ImageCacheService } = await import("../../../services/imageCacheService");
            const localBlob = await ImageCacheService.getImageBlob(imageUrl);
            if (!localBlob) throw new Error("Local image not found");
            blob = localBlob;
          } else {
            const res = await fetch(imageUrl);
            blob = await res.blob();
          }

          const base64 = await scanImageBase64(blob);

          const categories = liveState.categories;
          const dictionary = await getProductDictionary(supabase, userId);
          const profile = await getProfile(supabase, userId);
          const { useStore } = await import("../store");
          const state = useStore.getState();

          const result = await extractItemsFromImage(
            supabase,
            base64,
            categories,
            'tag',
            profile?.ai_profile,
            dictionary,
            unitSystem,
            massUnits,
            language,
            undefined, // No progressive addition, wait for confirmation
            false, // strict: false
            true // skipDelay: true for Chat tools
          );

          const itemsForAi = (result.items || []).map(i => {
            const { brand, product, variety, unit } = i;
            let { size } = i;

            // FIX: Manual Parsing for combined size/unit strings (e.g. "12oz" or "1.5lb")
            let rUnit = unit;
            if (typeof size === 'string' && !unit) {
              const m = size.match(/^([\d.]+)([a-zA-Z\s]+)$/);
              if (m) {
                size = parseFloat(m[1]);
                rUnit = m[2].trim().toLowerCase();
              } else if (!isNaN(parseFloat(size))) {
                size = parseFloat(size);
              }
            } else if (typeof size === 'string' && !isNaN(parseFloat(size))) {
              size = parseFloat(size);
            }

            // Pre-format for the confirmation card UI - respect grounded name if available
            const formattedName = i.name || formatDisplayName({ brand, product, variety, size, unit: rUnit });
            const protocolSlug = i.canonicalId || constructProtocolSlug({ brand, product, variety, size, unit: rUnit });
            
            return {
              ...i,
              size,
              unit: rUnit,
              name: formattedName,
              category: i.category || "Other",
              canonicalId: protocolSlug,
              embedding: i.embedding,
              imageUrl: imageUrl,
              upc: i.upc || null,
              gtin: i.gtin || null,
              plu_code: i.plu_code || null
            };
          }).filter(i => i.product || i.name);

          liveState.pendingExtractedItems = itemsForAi;
          return {
            success: itemsForAi.length > 0,
            itemCount: itemsForAi.length,
            message: itemsForAi.length > 0
              ? `${itemsForAi.length} items added to your basket. You can see them appearing live.${unitPriceCheckNote(itemsForAi, currencySymbol)}`
              : "Could not read item details."
          };
        } catch (err) {
          console.error("[CartAssistant] process_item_image tool error:", err);
          return { success: false, message: getExtractionErrorMessage(err) };
        }
      }
    }),

    defineTool({
      name: "process_receipt_image",
      description: "Extracts store info, date/time, and ALL line items from a shopping receipt image. A receipt is a full bill or statement with a list of items and a total. CRITICAL: Use this tool if the image appears to be a commercial receipt. If the image is clearly just a product photo, price tag, or shelf label, use 'process_item_image' instead. REQUIRED: whenever the uploaded image is a receipt, you MUST call this tool FIRST — regardless of whether the user mentions store, date, or metadata. Never read receipts with your own vision.",
      inputSchema: z.object({
        imageUrl: z.string().describe("The URL of the receipt image (from [img:URL] marker)"),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        isReceipt: z.boolean().optional(),
        itemCount: z.number().optional(),
        storeName: z.string().optional(),
        storeAddress: z.string().optional(),
        date: z.string().optional(),
        time: z.string().optional(),
        subtotal: z.number().optional(),
        totalAmount: z.number().optional(),
        message: z.string()
        // items intentionally omitted — staged in liveState to prevent LLM re-serialization hang
      }),
      tool: async ({ imageUrl }) => {
        const { supabase, userId } = getContext();

        try {
          console.log("[CartAssistant] process_receipt_image: Fetching", imageUrl);
          let blob: Blob;
          if (imageUrl.startsWith('local:') || imageUrl.startsWith('chat:')) {
            const { ImageCacheService } = await import("../../../services/imageCacheService");
            const localBlob = await ImageCacheService.getImageBlob(imageUrl);
            if (!localBlob) throw new Error("Local receipt image not found");
            blob = localBlob;
          } else {
            const res = await fetch(imageUrl);
            blob = await res.blob();
          }

          const base64 = await scanImageBase64(blob);

          const categories = liveState.categories;
          const dictionary = await getProductDictionary(supabase, userId);
          const profile = await getProfile(supabase, userId);
          const { useStore } = await import("../store");
          const state = useStore.getState();

          const result = await extractItemsFromImage(
            supabase,
            base64,
            categories,
            'receipt',
            profile?.ai_profile,
            dictionary,
            unitSystem,
            massUnits,
            language,
            undefined, // No progressive addition, wait for confirmation
            false, // strict: false
            true // skipDelay: true for Chat tools
          );

          console.log(`[CartAssistant] process_receipt_image extracted: storeName="${result.storeName}" date="${result.date}" time="${result.time}" items=${result.items?.length}`);

          // Only apply metadata if Gemini confirmed this is actually a receipt
          if (result.isReceipt) {
            if (liveState.applyReceiptMetadata) {
              await liveState.applyReceiptMetadata({
                storeName: result.storeName,
                storeAddress: result.storeAddress,
                date: result.date,
                time: result.time,
                subtotal: result.subtotal,
                totalAmount: result.totalAmount,
              }).catch(err => console.warn("[CartAssistant] process_receipt_image: applyReceiptMetadata failed:", err));
            }
            liveState.receiptMetadataSource = { storeName: result.storeName, storeAddress: result.storeAddress };
            liveState.setReceiptImageUrl(imageUrl);
          }

          const itemsForAi = (result.items || []).map(i => {
            const { brand, product, variety, unit } = i;
            let { size } = i;

            // FIX: Manual Parsing for combined size/unit strings (e.g. "12oz" or "1.5lb")
            let rUnit = unit;
            if (typeof size === 'string' && !unit) {
              const m = size.match(/^([\d.]+)([a-zA-Z\s]+)$/);
              if (m) {
                size = parseFloat(m[1]);
                rUnit = m[2].trim().toLowerCase();
              } else if (!isNaN(parseFloat(size))) {
                size = parseFloat(size);
              }
            } else if (typeof size === 'string' && !isNaN(parseFloat(size))) {
              size = parseFloat(size);
            }

            // Pre-format for the confirmation card UI - respect grounded name if available
            const formattedName = i.name || formatDisplayName({ brand, product, variety, size, unit: rUnit });
            const protocolSlug = i.canonicalId || constructProtocolSlug({ brand, product, variety, size, unit: rUnit });
            
            return {
              ...i,
              size,
              unit: rUnit,
              name: formattedName,
              category: i.category || "Other",
              canonicalId: protocolSlug,
              embedding: i.embedding,
              imageUrl: imageUrl,
              upc: i.upc || null,
              gtin: i.gtin || null,
              plu_code: i.plu_code || null
            };
          }).filter(i => i.product || i.name);

          if (result.isReceipt) {
            liveState.pendingReceiptItems = itemsForAi;
          } else {
            liveState.pendingExtractedItems = itemsForAi;
          }

          return {
            success: result.isReceipt === true,
            isReceipt: result.isReceipt,
            itemCount: itemsForAi.length,
            // items intentionally omitted — staged in liveState
            storeName: result.isReceipt ? result.storeName : undefined,
            storeAddress: result.isReceipt ? result.storeAddress : undefined,
            date: result.isReceipt ? result.date : undefined,
            time: result.isReceipt ? result.time : undefined,
            subtotal: result.isReceipt ? result.subtotal : undefined,
            totalAmount: result.isReceipt ? result.totalAmount : undefined,
            message: result.isReceipt
              ? `Receipt scanned: ${itemsForAi.length} items found at "${result.storeName}" on ${result.date} (Subtotal: ${currencySymbol}${result.subtotal?.toFixed(2) || '?'}). MANDATORY NEXT STEP: call 'set_receipt_metadata' NOW with storeName="${result.storeName}", storeAddress="${result.storeAddress || ''}", date="${result.date}", time="${result.time}", subtotal=${result.subtotal ?? 'null'}, totalAmount=${result.totalAmount ?? 'null'}, itemCount=${itemsForAi.length}. Do NOT proceed to adding items yet — the user must confirm the location and date first.${unitPriceCheckNote(itemsForAi, currencySymbol)}`
              : `Product image (not a receipt): ${itemsForAi.length} items staged. Call 'add_all_items_to_basket' with no arguments. DO NOT call set_receipt_metadata or any store metadata tools.${unitPriceCheckNote(itemsForAi, currencySymbol)}`
          };
        } catch (err) {
          console.error("[CartAssistant] process_receipt_image tool error:", err);
          return { success: false, isReceipt: false, message: getExtractionErrorMessage(err) };
        }
      }
    }),

    defineTool({
      name: "geocode_store_location",
      description: "Resolves a store name and address into precise GPS coordinates and a canonical address using high-velocity geocoding (2.5-flash). Call this if the store address from a receipt is vague or if you need to confirm a location BEFORE calling set_receipt_metadata.",
      inputSchema: z.object({
        storeName: z.string().describe("Name of the store"),
        storeAddress: z.string().describe("Approximate address or location string"),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        lat: z.number().optional(),
        lng: z.number().optional(),
        address: z.string().optional(),
        url: z.string().optional(),
        message: z.string()
      }),
      tool: async ({ storeName, storeAddress }) => {
        try {
          const result = await geocodeStore(storeName, storeAddress);
          if (result) {
            return {
              success: true,
              lat: result.lat,
              lng: result.lng,
              address: storeAddress, // We keep the original address or we could return a normalized one if geocodeStore returned it
              url: result.url,
              message: `Location resolved to ${result.lat}, ${result.lng}.`
            };
          }
          return { success: false, message: "Could not resolve coordinates for this location." };
        } catch (err) {
          console.error("[CartAssistant] geocode_store_location error:", err);
          return { success: false, message: "Geocoding failed." };
        }
      }
    }),

    defineTool({
      name: "set_receipt_metadata",
      description: "Sets store name and trip date/time in the basket UI. Uses high-velocity geocoding (2.5-flash) to resolve the location if coordinates are not provided. Call this immediately after process_receipt_image returns storeName or date — BEFORE item confirmation.",
      inputSchema: z.object({
        storeName: z.string().describe("Name of the store"),
        storeAddress: z.string().optional().describe("Address of the store"),
        date: z.string().describe("Date (YYYY-MM-DD)"),
        time: z.string().optional().describe("Time (HH:mm 24h)"),
        subtotal: z.number().optional().describe("The subtotal on the receipt (before tax)"),
        totalAmount: z.number().optional().describe("The grand total on the receipt"),
        lat: z.number().optional().describe("Latitude for the store"),
        lng: z.number().optional().describe("Longitude for the store"),
      }),
      outputSchema: z.object({
        success: z.boolean(),
        message: z.string(),
        nonce: z.string().optional(),
        storeName: z.string().optional(),
        storeAddress: z.string().optional(),
        date: z.string().optional(),
        time: z.string().optional(),
        itemCount: z.number().optional(),
      }),
      tool: async (params) => {
        const { storeName, storeAddress, date, time, subtotal, totalAmount, lat, lng } = params;
        console.log(`[CartAssistant] set_receipt_metadata called: storeName="${storeName}" date="${date}" time="${time}" subtotal=${subtotal} totalAmount=${totalAmount} coords=${lat},${lng}`);
        try {
          // Delegate to applyReceiptMetadata — the same function the Import button uses.
          if (liveState.applyReceiptMetadata) {
            await liveState.applyReceiptMetadata({ 
              storeName, 
              storeAddress, 
              date, 
              time,
              subtotal,
              totalAmount,
              lat,
              lng,
              skipStoreResolution: (liveState.pendingReceiptItems?.length || 0) > 0 &&
                (liveState.receiptMetadataSource?.storeName || '') === (storeName || '') &&
                (liveState.receiptMetadataSource?.storeAddress || '') === (storeAddress || ''),
            });
            console.log(`[CartAssistant] set_receipt_metadata: applyReceiptMetadata completed`);
          } else {
            // Fallback: direct setters if applyReceiptMetadata isn't wired yet
            if (subtotal) {
              liveState.setReceiptSubtotal(subtotal);
            }
            if (storeName?.trim()) {
              const storeObj = { 
                name: storeName.trim(), 
                address: storeAddress?.trim() || undefined,
                coordinates: (lat && lng) ? { lat, lng } : undefined
              };
              liveState.setCurrentStore({ ...storeObj, name: getFormattedStoreDisplayName(storeObj) });
            }
            if (date?.trim()) {
              const [year, month, day2] = date.split('-').map(Number);
              const dt = new Date(year, month - 1, day2);
              if (time?.trim()) { const [h, m] = time.split(':').map(Number); dt.setHours(h, m, 0, 0); }
              if (!isNaN(dt.getTime())) { liveState.setTripDateTime(dt); liveState.setIsTimeManual(true); }
            }
          }

          const pendingItems = liveState.pendingReceiptItems || [];
          const nonce = `rmeta_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
          return {
            success: true,
            nonce,
            storeName: storeName || undefined,
            storeAddress: storeAddress || undefined,
            date: date || undefined,
            time: time || undefined,
            subtotal: subtotal || undefined,
            totalAmount: totalAmount || undefined,
            itemCount: pendingItems.length,
            // Do NOT return items array — AI re-serializing it causes "Generating response" hang
            message: `Metadata set (Subtotal: ${currencySymbol}${subtotal?.toFixed(2) || '?'}). MANDATORY: Render 'ReceiptMetadataCard' NOW with props: storeName="${storeName}", storeAddress="${storeAddress}", date="${date}", time="${time}", nonce="${nonce}", itemCount=${pendingItems.length}. STOP — do not call add_all_items_to_basket yet. Wait for the user to click "Extract Items" on the card.`,
          };
        } catch (err) {
          console.error('[CartAssistant] set_receipt_metadata error:', err);
          return { success: false, message: 'Failed to update trip context.' };
        }
      }
    }),

    defineTool({
      name: "create_new_category",
      description: "Creates a new spending category for the user. USE THIS if a desired category (like 'Vehicle') doesn't exist.",
      inputSchema: z.object({
        name: z.string().describe("Name of the category"),
        color: z.string().optional().default("#10b981").describe("Hex color code"),
        emoji: z.string().optional().default("🏷️").describe("Emoji icon"),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string() }),
      tool: async ({ name, color = "#10b981", emoji = "🏷️" }) => {
        const { supabase, userId } = getContext();
        const { useStore } = await import("../store");
        const state = useStore.getState();
        const current = state.categories;
        const updated = [...current, { name, color, emoji }];
        const { setCategories } = await import("../../../services/supabaseService");
        await setCategories(supabase, userId, updated);
        state.setCategories(updated);
        
        return { success: true, message: t('categories.created_success', { name }) };
      }
    }),

    defineTool({
      name: "update_item_in_current_basket",
      description: `Modifies an existing item in the user's active shopping basket. You MUST provide the currentName to identify the item. Use brand, product, variety, size, unit for structured updates.`,
      inputSchema: z.object({
        currentName: z.string().describe("The name of the item as it currently appears in the basket"),
        updates: z.object({
          brand: z.string().optional().nullable(),
          product: z.string().optional(),
          variety: z.string().optional().nullable(),
          size: z.number().optional().nullable(),
          unit: z.string().optional().nullable(),
          price: z.number().optional(),
          quantity: z.number().optional(),
          category: z.string().optional().describe("The category name. MUST match one from 'available_categories'."),
        }).describe("The fields to update"),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string() }),
      tool: async ({ currentName, updates }) => {
        const { useStore } = await import("../store");
        const state = useStore.getState();
        const items = state.operatingContext === 'personnel' ? state.latticeItems : state.items;
        const item = items.find(i => i.name.toLowerCase().includes(currentName.toLowerCase()));
        if (item) {
          const merged = { ...item, ...updates };
          const newName = formatDisplayName(merged);
          const newSlug = constructProtocolSlug(merged);
          
          state.updateItem(item.id, { 
            ...updates, 
            name: newName, 
            canonicalId: newSlug
          });
          return { success: true, message: t('product.updated_success', { name: item.name, updates: JSON.stringify(updates) }) };
        }
        return { success: false, message: t('product.not_found', { name: currentName }) };
      }
    }),

    defineTool({
      name: "set_current_store",
      description: "Sets the user's active merchant for the current shopping trip. Use full metadata (address, lat, lng, url) if available from history to ensure accuracy. CONFIRMATION REQUIRED: Always call 'request_confirmation' and show ConfirmActionCard before calling.",
      inputSchema: z.object({
        name: z.string().describe("Name of the store"),
        address: z.string().optional().describe("Physical address of the store"),
        lat: z.number().optional().describe("Latitude coordinate"),
        lng: z.number().optional().describe("Longitude coordinate"),
        url: z.string().optional().describe("Google Maps URL or merchant URL"),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string(), store: z.any().optional() }),
      tool: async ({ name, address, lat, lng, url }) => {
        const { supabase, userId } = getContext();
        try {
          let finalStore: any;

          // If coordinates are provided, prioritize using them directly
          if (lat !== undefined && lng !== undefined) {
            finalStore = {
              name,
              address,
              coordinates: { lat, lng },
              url
            };
          } else {
            // Fallback to search/geocode if coordinates are missing
            const searchQuery = address ? `${name}, ${address}` : name;
            const searchResults = await searchStores(searchQuery);
            if (searchResults && searchResults.length > 0) {
              finalStore = searchResults[0];
            } else {
              const { geocodeStore } = await import("../../../services/geminiService");
              const geo = await geocodeStore(name, address || "");
              if (geo) {
                finalStore = {
                  name,
                  address,
                  coordinates: { lat: geo.lat, lng: geo.lng },
                  url: geo.url
                };
              } else {
                finalStore = { name, address, url };
              }
            }
          }
          
          // Apply standard formatting (Store Name, Street Address)
          const formattedStore = {
            ...finalStore,
            name: getFormattedStoreDisplayName(finalStore)
          };

          liveState.setCurrentStore(formattedStore);

          return {
            success: true,
            message: t('chat.set_current_store_success', { name: formattedStore.name }),
            store: formattedStore
          };
        } catch (err) {
          console.error("[CartAssistant] set_current_store tool error:", err);
          const fallbackStore = { name, address, url };
          const formattedFallback = {
            ...fallbackStore,
            name: getFormattedStoreDisplayName(fallbackStore)
          };
          liveState.setCurrentStore(formattedFallback);
          return { success: true, message: t('chat.set_current_store_success', { name: formattedFallback.name }) };
        }
      }
    }),

    defineTool({
      name: "set_trip_date_time",
      description: "Sets the date and time for the current shopping trip. Use this when re-adding a historical trip to ensure the archive matches the original purchase date.",
      inputSchema: z.object({
        isoDate: z.string().describe("The ISO 8601 date string (e.g., 2024-03-10T14:30:00Z)"),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string() }),
      tool: async ({ isoDate }) => {
        try {
          const date = new Date(isoDate);
          if (isNaN(date.getTime())) {
            return { success: false, message: "Invalid date format." };
          }
          liveState.setTripDateTime(date);
          liveState.setIsTimeManual(true);
          return { success: true, message: `Trip date/time set to ${date.toLocaleString()}` };
        } catch (err) {
          return { success: false, message: "Failed to set date/time." };
        }
      }
    }),

    defineTool({
      name: "get_shopping_history",
      description: "Fetches a summary of past shopping trips.",
      inputSchema: z.object({
        limit: z.number().optional().default(5).describe("Number of entries to fetch"),
      }),
      outputSchema: z.any(),
      tool: async ({ limit = 5 }) => {
        const { supabase, userId } = getContext();
        const history = await getHistory(supabase, userId);
        return history.slice(0, limit).map(h => ({
          id: h.id,
          date: h.date,
          totalCost: h.totalCost,
          name: h.storeInfo?.name || t('common.unknown_store'),
          storeName: h.storeInfo?.name || t('common.unknown_store'),
          address: h.storeInfo?.address,
          lat: h.storeInfo?.coordinates?.lat,
          lng: h.storeInfo?.coordinates?.lng,
          url: h.storeInfo?.url,
          itemCount: h.items.length
        }));
      },
    }),

    defineTool({
      name: "get_shopping_trip_details",
      description: "Fetches full details for a specific shopping trip ID.",
      inputSchema: z.object({
        entryId: z.string().describe("The ID of the shopping history entry"),
      }),
      outputSchema: z.any(),
      tool: async ({ entryId }) => {
        const { supabase, userId } = getContext();
        const entry = await getHistoryEntry(supabase, userId, entryId);
        if (!entry) return { success: false, message: "Trip not found" };
        return {
          id: entry.id,
          date: entry.date,
          totalCost: entry.totalCost,
          storeInfo: entry.storeInfo ? {
            name: entry.storeInfo.name,
            address: entry.storeInfo.address,
            lat: entry.storeInfo.coordinates?.lat,
            lng: entry.storeInfo.coordinates?.lng,
            url: entry.storeInfo.url
          } : undefined,
          items: (entry.items || []).map(i => ({
            name: i.name,
            price: i.price,
            quantity: i.quantity,
            category: i.category,
            unit: i.unit,
            canonicalId: i.canonicalId,
            imageUrl: i.imageUrl,
            brand: i.brand,
            product: i.product,
            variety: i.variety,
            size: i.size,
            upc: i.upc,
            gtin: i.gtin,
            embedding: i.embedding
          }))
        };
      },
    }),

    defineTool({
      name: "readd_trip_to_basket",
      description: "Atomically re-adds all items from a historical trip to the current basket and sets the active store location. This tool ALWAYS appends items and NEVER clears the existing basket.",
      inputSchema: z.object({
        entryId: z.string().describe("The ID of the shopping history entry to re-add"),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string(), count: z.number().optional() }),
      tool: async ({ entryId }) => {
        const { supabase, userId } = getContext();
        const entry = await getHistoryEntry(supabase, userId, entryId);
        if (!entry) return { success: false, message: "Trip not found", count: 0 };

        const { useStore } = await import("../store");
        const state = useStore.getState();

        // 1. Restore Store Location (but keep current time)
        if (entry.storeInfo) {
          const formattedStore = {
            ...entry.storeInfo,
            name: getFormattedStoreDisplayName(entry.storeInfo)
          };
          liveState.setCurrentStore(formattedStore);
        }

        // 2. Add all items one by one (Appending)
        let addedCount = 0;
        for (const item of (entry.items || [])) {
          // Destructure to match addItem signature
          const { brand, product, variety, size, unit, price, category, quantity = 1, imageUrl, name, canonicalId, embedding, shelfLife, upc, gtin, plu_code } = item as any;
          
          state.addItem(
            product || name || 'Unknown Item', 
            price || 0, 
            category || 'Other', 
            imageUrl || undefined, 
            Math.max(1, quantity || 1), 
            canonicalId || undefined, 
            unit || undefined,
            brand, 
            product, 
            variety, 
            size, 
            shelfLife, 
            embedding,
            plu_code || undefined, 
            undefined, 
            upc, 
            gtin
          );
          addedCount++;
        }

        return { 
          success: true, 
          message: `Successfully re-added ${addedCount} items from ${entry.storeInfo?.name || 'historical trip'} to your current basket.`,
          count: addedCount
        };
      }
    }),

    defineTool({
      name: "get_current_basket_contents",
      description: "Retrieves the list of items currently in the user's active shopping basket.",
      inputSchema: z.object({}),
      outputSchema: z.any(),
      tool: async () => {
        const { useStore } = await import("../store");
        const state = useStore.getState();
        const items = state.operatingContext === 'personnel' ? state.latticeItems : state.items;
        return items.map(i => ({
          id: i.id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          category: i.category,
          unit: i.unit,
          canonicalId: i.canonicalId,
          imageUrl: i.imageUrl,
          // Shelf unit-price check (when the tag printed a unit price).
          unitPriceCheck: (() => { const c = getUnitPriceCheck(i); return c ? describeUnitPriceCheck(i.name, c, currencySymbol) : undefined; })(),
        }));
      },
    }),

    defineTool({
      name: "check_unit_price",
      description: "Answers 'is this unit price right?' for an item in the basket. CartLens redoes the shelf tag's math from the item's current price and size: price ÷ size, converted into the tag's unit (oz/lb, g/kg/per 100 g, ml/l, fl oz, ct/per 100), against the unit price printed on the tag. Uses the unit price read from the item's tag photo; if the user tells you what the tag says (e.g. '$0.31/oz'), pass it as shelfUnitPrice + shelfUnitPriceUnit. When the unit price doesn't add up and the user says the tag is right (\"looks right\", the size was misread), call it with applySizeFix=true: it sets the item's size to the one the tag implies, which clears the mismatch. Word the answer neutrally ('the unit price doesn't add up'), never as a store error.",
      inputSchema: z.object({
        itemName: z.string().describe("The basket item's name (or part of it)"),
        shelfUnitPrice: z.number().optional().describe("The unit price the user says the tag prints, in currency units (e.g. 0.31)"),
        shelfUnitPriceUnit: z.string().optional().describe("Its unit as printed (e.g. 'oz', 'lb', '100 g', '100 ct', 'fl oz')"),
        applySizeFix: z.boolean().optional().describe("True when the user confirms the tag is right: set the size to match the tag's unit price"),
      }),
      outputSchema: z.object({ success: z.boolean(), status: z.string().optional(), message: z.string() }),
      tool: async ({ itemName, shelfUnitPrice, shelfUnitPriceUnit, applySizeFix }) => {
        const { useStore } = await import("../store");
        const state = useStore.getState();
        const items = state.operatingContext === 'personnel' ? state.latticeItems : state.items;
        // Every word of the name as the user said it ("Oak Valley peanut butter"
        // finds "Oak Valley (Peanut Butter, Creamy, 16oz)").
        const words = (text: string) => text.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
        const wanted = words(itemName);
        let item = items.find(i => i.name.toLowerCase().includes(itemName.toLowerCase()))
          || items.find(i => { const have = words(i.name); return wanted.length > 0 && wanted.every(w => have.includes(w)); });
        if (!item) return { success: false, message: `No basket item matches "${itemName}".` };

        if (shelfUnitPrice != null && shelfUnitPriceUnit) {
          // What the user read off the tag: saved like a tag photo's reading.
          const tag = readShelfUnitPrice({
            price: item.price, size: item.size, unit: item.unit,
            shelfUnitPrice, shelfUnitPriceUnit,
            sizeConfidence: 'high', unitPriceConfidence: 'high',
          }, 'tag');
          const check = tag && compareUnitPrice(tag, item);
          if (!tag || !check) {
            return { success: false, message: `Can't compare: "${item.name}" is ${item.size ?? '?'} ${item.unit ?? ''} and the tag's unit is "${shelfUnitPriceUnit}" (they don't convert, e.g. weight oz vs fluid oz, or the item has no size). Ask the user to check the size on the package.` };
          }
          item = { ...item, metadata: { ...(item.metadata || {}), unit_price_check: tag } };
          state.updateItem(item.id, { metadata: item.metadata });
        }

        const check = getUnitPriceCheck(item);
        if (!check) return { success: false, message: `No unit price was read from a tag for "${item.name}" (or its size can't be compared with it). Ask the user what the shelf tag's unit price says (then call this tool again with it), or to photograph the tag.` };

        if (applySizeFix && check.status === 'mismatch') {
          const fix = sizeFixUpdates(item);
          if (!fix) return { success: false, status: check.status, message: `Couldn't work out a size for "${item.name}" from the tag. Ask the user to check the size on the package and edit it.` };
          state.updateItem(item.id, fix);
          return { success: true, status: 'match', message: `Set the size of "${item.name}" to ${fix.size} ${fix.unit}, which matches the tag's unit price (${describeUnitPriceCheck(fix.name || item.name, getUnitPriceCheck({ ...item, ...fix })!, currencySymbol)}). Tell the user the size was updated.` };
        }
        return { success: true, status: check.status, message: `${describeUnitPriceCheck(item.name, check, currencySymbol)}${check.status === 'mismatch' ? ' The item shows an amber "Unit price doesn\'t add up" icon. If the user says the tag is right, call check_unit_price again with applySizeFix=true.' : ''}` };
      },
    }),

    defineTool({
      name: "get_user_profile_details",
      description: "Fetches the current profile: the HUMAN USER's name (userName), the separate ASSISTANT's name (assistantName), shopping style, plan, bio, allergies (allAllergies), household, pets, children (with turnsThisYear), vehicles, preferences, store memberships, ZIP, language and currency. Only filled-in fields are returned; anything missing is unknown. Required before answering any profile question, including what is your name or what did I name you. This result replaces any older profile result in the chat.",
      inputSchema: z.object({}),
      outputSchema: z.any(),
      tool: async () => {
        const { supabase, userId } = getContext();
        const profile = await getProfile(supabase, userId);
        return profileSummaryForAssistant(profile, useStore.getState().pendingProfilePatch);
      },
    }),

    defineTool({
      name: "request_confirmation",
      description: "Initiates a confirmation flow for data-changing actions. You can call this for a single item or a batch of items. Always include structured fields (brand, product, size, imageUrl) in the items array for high-fidelity confirmation.",
      inputSchema: z.object({
        actionDescription: z.string().describe("What is being confirmed."),
        toolName: z.string().describe("The tool to be executed."),
        items: z.array(ShoppingItemSchema.pick({
          name: true,
          brand: true,
          product: true,
          variety: true,
          size: true,
          unit: true,
          price: true,
          category: true,
          quantity: true,
          imageUrl: true,
          canonicalId: true,
          upc: true,
          gtin: true
        }).partial()).optional().describe("Item list for confirmation card"),
      }),
      outputSchema: z.object({ status: z.string(), action: z.string(), nonce: z.string(), toolName: z.string(), items: z.any().optional() }),
      tool: async ({ actionDescription, toolName, items }) => {
        const nonce = Date.now().toString(36) + Math.random().toString(36).substring(2, 7);
        
        // Stage the items so they can be processed by tools like add_all_items_to_basket.
        // Guard: don't overwrite already-hydrated items (which carry embeddings) with the
        // lightweight card representation that request_confirmation receives (no embedding).
        if (items && items.length > 0) {
          const alreadyHydrated = liveState.pendingExtractedItems?.some((i: any) => i.embedding != null);
          if (!alreadyHydrated) {
            liveState.pendingExtractedItems = items as any;
          }
        }

        return { 
          status: "AWAITING_VISUAL_CONFIRMATION_CARD",
          action: actionDescription,
          nonce,
          toolName,
          items
        };
      },
    }),

    defineTool({
      name: "add_single_item_to_current_basket",
      description: `Adds ONE manually-described item to the basket — for items the user types or describes in conversation ONLY. NEVER call this after processing any image or receipt (use add_all_items_to_basket for those). Use discrete fields: brand, product, variety, size, unit for precision.`,
      inputSchema: ShoppingItemSchema.pick({
        brand: true,
        product: true,
        variety: true,
        size: true,
        unit: true,
        price: true,
        category: true,
        quantity: true,
        imageUrl: true,
        name: true,
        canonicalId: true,
        embedding: true,
        shelfLife: true,
        upc: true,
        gtin: true,
        plu_code: true
      }).partial({
        brand: true,
        variety: true,
        size: true,
        quantity: true,
        imageUrl: true,
        unit: true,
        name: true,
        canonicalId: true,
        embedding: true,
        shelfLife: true,
        upc: true,
        gtin: true,
        plu_code: true
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string() }),
      tool: async ({ brand, product, variety, size, unit, price, category, quantity = 1, imageUrl, name, canonicalId, embedding, shelfLife, upc, gtin, plu_code }) => {
        const finalQuantity = Math.max(1, quantity || 1);
        
        const { useStore } = await import("../store");
        const state = useStore.getState();
        
        state.addItem(
          product || name || 'Unknown Item', 
          price || 0, 
          category || 'Other', 
          imageUrl || undefined, 
          finalQuantity, 
          canonicalId || undefined, 
          unit || undefined,
          brand,
          product,
          variety,
          size,
          shelfLife,
          embedding,
          plu_code || undefined, 
          undefined, // metadata
          upc,
          gtin
        );
        return { 
          success: true, 
          message: t('basket.items_captured_count', { count: finalQuantity }) 
        };
      },
    }),

    defineTool({
      name: "add_all_items_to_basket",
      description: "Adds ALL Gemini-extracted items from an image to the basket. Call with NO arguments — items are staged internally with full structured data (brand, product, variety, size, unit) from Gemini. MANDATORY for both receipts (after ReceiptMetadataCard confirmation) and product/tag images (after ConfirmActionCard). Never use add_single_item_to_current_basket for image-extracted items.",
      inputSchema: z.object({}),
      outputSchema: z.object({ success: z.boolean(), count: z.number(), message: z.string() }),
      tool: async () => {
        const { useStore } = await import("../store");
        const state = useStore.getState();
        // Receipts stage into pendingReceiptItems; product/tag images into pendingExtractedItems.
        // Prefer receipt items to avoid stale product-scan data shadowing them.
        const staged = liveState.pendingReceiptItems?.length
          ? liveState.pendingReceiptItems
          : liveState.pendingExtractedItems;

        // Best-effort: attach a clean stock/product photo per item (item.imageUrl, when present,
        // stays the actual scanned proof shot — this is purely a display upgrade on top of it).
        let stockImages: Record<string, string> = {};
        try {
          const { fetchProductImages } = await import("../marketDiscovery");
          stockImages = await fetchProductImages(
            staged.map((i: any) => ({ name: i.name || i.product, brand: i.brand }))
          );
        } catch (imgErr) {
          console.warn("[add_all_items_to_basket] stock image lookup failed (non-fatal):", imgErr);
        }

        let added = 0;
        for (const item of staged) {
          if (!item.product && !item.name) continue;
          const lookupName = item.name || item.product;
          const stockImageUrl = stockImages[lookupName];
          state.addItem(
            item.product || item.name || 'Unknown Item',
            item.price || 0,
            item.category || 'Other',
            item.imageUrl || undefined,
            Math.max(1, item.quantity || 1),
            item.canonicalId || undefined,
            item.unit || undefined,
            item.brand,
            item.product,
            item.variety,
            item.size,
            item.shelfLife,
            item.embedding,
            item.plu_code || undefined,
            stockImageUrl ? { ...(item.metadata || {}), stockImageUrl } : item.metadata,
            item.upc,
            item.gtin
          );
          added++;
        }
        // Clear staging areas
        liveState.pendingExtractedItems = [];
        liveState.pendingReceiptItems = [];
        return { success: added > 0, count: added, message: t('basket.items_captured_count', { count: added }) };
      },
    }),

    defineTool({
      name: "remove_item_from_current_basket",
      description: "Removes an item from the basket by name.",
      inputSchema: z.object({
        name: z.string().describe("Name of the item to remove"),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string() }),
      tool: async ({ name }) => {
        const { useStore } = await import("../store");
        const state = useStore.getState();
        const items = state.operatingContext === 'personnel' ? state.latticeItems : state.items;
        const item = items.find(i => i.name.toLowerCase().includes(name.toLowerCase()));
        if (item) {
          state.removeItem(item.id);
          return { success: true, message: `Removed ${item.name}` };
        }
        return { success: false, message: `Item ${name} not found in basket.` };
      },
    }),

    defineTool({
      name: "clear_current_basket",
      description: "Empties the active shopping basket. Use only when the user explicitly asks to 'wipe', 'clear', or 'replace' their basket. Do NOT call this when repeating or re-adding items from history unless explicitly asked.",
      inputSchema: z.object({}),
      outputSchema: z.object({ success: z.boolean(), message: z.string().optional() }),
      tool: async () => {
        const { useStore } = await import("../store");
        useStore.getState().clearBasket();
        return { success: true, message: "Basket cleared successfully." };
      },
    }),

    defineTool({
      name: "archive_current_basket",
      description: "Opens the archive confirmation modal so the user can review the full trip details before saving to the Lattice. Call this when the user asks to archive, save, or finalize their shopping trip. The modal handles the actual save — do NOT call saveToHistory directly.",
      inputSchema: z.object({}),
      outputSchema: z.object({ success: z.boolean(), message: z.string().optional() }),
      tool: async () => {
        if (liveState.triggerArchiveModal) {
          liveState.triggerArchiveModal();
          return { success: true, message: "Archive confirmation modal is now open. The user can review their trip and confirm to save it to the Lattice." };
        }
        return { success: false, message: "UI bridge not initialized." };
      },
    }),

    defineTool({
      name: "get_all_categories",
      description: "Provides a list of all spending categories.",
      inputSchema: z.object({}),
      outputSchema: z.any(),
      tool: async () => {
        try {
          const { useStore } = await import("../store");
          return useStore.getState().categories;
        } catch (err) {
          console.error("[CartAssistant] get_all_categories failed:", err);
          return [];
        }
      },
    }),

    defineTool({
      name: "get_master_ai_insight",
      description: "Retrieves the user's last generated master AI insight.",
      inputSchema: z.object({}),
      outputSchema: z.any(),
      tool: async () => {
        try {
          const { supabase, userId } = getContext();
          return await getMasterInsight(supabase, userId);
        } catch (err) {
          console.error("[CartAssistant] get_master_ai_insight failed:", err);
          return null;
        }
      },
    }),

    defineTool({
      name: "suggest_category_for_item",
      description: "Uses AI to suggest a category for a given item name.",
      inputSchema: z.object({
        itemName: z.string().describe("Name of the item to suggest a category for"),
      }),
      outputSchema: z.object({ suggestedCategory: z.string() }),
      tool: async ({ itemName }) => {
        try {
          const { useStore } = await import("../store");
          const catNames = useStore.getState().categories.map(c => c.name);
          const suggested = await suggestCategory(itemName, catNames);
          return { suggestedCategory: suggested || "Other" };
        } catch (err) {
          console.error("[CartAssistant] suggest_category_for_item failed:", err);
          return { suggestedCategory: "Other" };
        }
      },
    }),

    defineTool({
      name: "delete_past_history_entry",
      description: "Deletes a historical shopping trip by ID.",
      inputSchema: z.object({
        entryId: z.string().describe("The ID of the history entry to delete"),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string().optional() }),
      tool: async ({ entryId }) => {
        try {
          const { supabase, userId } = getContext();
          await deleteHistoryEntry(supabase, userId, entryId);
          const { useStore } = await import("../store");
          useStore.getState().setHistory(prev => prev.filter(h => h.id !== entryId));
          return { success: true, message: "History entry deleted successfully." };
        } catch (err) {
          console.error("[CartAssistant] delete_past_history_entry failed:", err);
          return { success: false, message: "Failed to delete history entry." };
        }
      },
    }),

    defineTool({
      name: "update_user_profile",
      description: "Updates the user's profile (assistant name and style, household, pets, children, vehicles, allergies, preferences, store memberships). CONFIRMATION REQUIRED. Send only what changes. Values in list fields are ADDED to what's there (a vehicle or membership with the same name is updated in place); to take something out of a list, use `remove`. To add a fact to the bio, use appendToBio; use replaceBio only when the user asks to rewrite the whole bio. ZIP, language and currency are changed in Settings, not here.",
      inputSchema: z.object({
        updates: AiProfileBaseSchema.pick({ assistant: true, domains: true, loyalty_programs: true }).partial().optional()
          .describe("Fields to set; lists are added to. Example: { domains: { dependents: { species: ['dog'] } } }"),
        appendToBio: z.string().optional().describe("A sentence to add to the end of the user's bio."),
        replaceBio: z.string().optional().describe("The whole new bio. Only when the user asks to rewrite it."),
        remove: z.array(z.object({
          path: z.string().describe("The list field, e.g. domains.grocery.allergies, domains.dependents.species, domains.automotive.vehicles, loyalty_programs"),
          values: z.array(z.string()).describe("The entries to remove (for vehicles or memberships: their nickname, make, model or store name)"),
        })).optional().describe("Entries to take out of list fields."),
      }),
      outputSchema: z.object({ success: z.boolean(), message: z.string(), profile: z.any() }),
      tool: async ({ updates, appendToBio, replaceBio, remove }) => {
        const { supabase, userId } = getContext();
        const current = await getProfile(supabase, userId);
        if (!current) throw new Error(t('chat.upload_error_auth'));
        const { pendingProfilePatch, markProfilePatched } = useStore.getState();
        const base = mergePersonalization(current.ai_profile || {}, pendingProfilePatch?.ai_profile || {});
        const aiProfile = applyAssistantProfileUpdate(base, { updates, appendToBio, replaceBio, remove });
        // Only ai_profile is sent: patch_profile merges it server-side, so settings,
        // plan and other columns are never rewritten from this (possibly older) copy.
        await updateProfile(supabase, userId, { ai_profile: aiProfile } as any);
        markProfilePatched({ ai_profile: aiProfile });
        return {
          success: true,
          message: "Profile updated. Tell the user what changed.",
          profile: profileSummaryForAssistant({ ...current, ai_profile: aiProfile }, { settings: pendingProfilePatch?.settings }),
        };
      },
    }),

    defineTool({
      name: "find_nearby_stores",
      description: "Finds retail locations near specific coordinates.",
      inputSchema: z.object({
        lat: z.number().optional().describe("Latitude"),
        lng: z.number().optional().describe("Longitude"),
      }),
      outputSchema: z.any(),
      tool: async ({ lat, lng }) => {
        try {
          if (typeof lat !== 'number' || typeof lng !== 'number') {
            return { success: false, message: "Location coordinates are missing." };
          }
          const stores = await getNearbyStores(lat, lng);
          return stores || [];
        } catch (err) {
          console.error("[CartAssistant] find_nearby_stores failed:", err);
          return { success: false, message: "Could not find stores in this area." };
        }
      },
    }),

    defineTool({
      name: "get_item_price_history",
      description: "Returns the unit price the user paid for a specific item on each recorded purchase (date, price, store). Use for price trends (ItemPriceHistoryLineChart). For how much the user SPENT on an item, use get_item_spending_by_store instead — unit prices are not spending.",
      inputSchema: z.object({
        itemName: z.string().describe("Name of the item to look up"),
      }),
      outputSchema: z.any(),
      tool: async ({ itemName }) => {
        try {
          // Per-purchase points, each with exactly one store. The aggregated RPC
          // below groups by day and comma-joins store names (which themselves
          // contain commas), so it is only a fallback.
          const purchases = await fetchItemPurchases(itemName);
          if (purchases.length > 0) {
            return purchases.map((p) => ({ date: p.date, price: p.unitPrice, store: p.store, quantity: p.quantity, name: p.name }));
          }

          const { supabase, userId } = getContext();
          const { getHistory } = await import('../../../services/supabaseService');

          // A single conceptual item (e.g. "peanuts") is often split across several
          // distinct product_canonical rows - different brand/size OCR reads across
          // receipts (e.g. "Sunny Select Peanuts...60oz" vs "...60") each get their
          // own canonical row since canonical_id is exact-spec, not fuzzy. Matching
          // only the first row (old behavior: .limit(1), unordered) silently dropped
          // every purchase recorded under any other variant, which is why a real
          // 6-month history could come back as a single data point. Aggregate across
          // every matching variant instead.
          const { data: canonicals } = await supabase
            .from('product_canonical')
            .select('id, slug')
            .ilike('name', `%${itemName}%`)
            .limit(15);

          if (canonicals && canonicals.length > 0) {
            try {
              const perVariant = await Promise.all(
                canonicals
                  .filter((c) => c.slug)
                  .map((c) => fetchItemPriceHistory(supabase, userId, c.slug))
              );
              const combined = perVariant.flat().sort((a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime());
              if (combined.length > 0) return combined;
            } catch (e) {
              console.warn("Optimized history fetch failed, falling back...");
            }
          }
          const { data: indexedHistory, error: indexedError } = await supabase
            .from('price_history')
            .select(`price, price_date, stores ( name )`)
            .ilike('item_name', `%${itemName}%`)
            .order('price_date', { ascending: false });
          if (!indexedError && indexedHistory && indexedHistory.length > 0) {
            return indexedHistory.map(ph => ({
              date: ph.price_date,
              price: ph.price,
              store: (ph.stores as any)?.name || "Unknown Store"
            }));
          }
          const history = await getHistory(supabase, userId);
          const matches = history.flatMap(trip => 
            trip.items
              .filter(item => item.name.toLowerCase().includes(itemName.toLowerCase()))
              .map(item => ({
                date: trip.date,
                price: item.price,
                store: trip.storeInfo?.name || "Unknown Store",
                quantity: item.quantity
              }))
          );
          return matches;
        } catch (err) {
          console.error("[CartAssistant] get_item_price_history failed:", err);
          return [];
        }
      },
    }),

    defineTool({
      name: "get_item_spending_by_store",
      description: "Totals how much the user has spent on a specific item at each store, from their own recorded purchases (unit price × quantity), including each store's address and coordinates. Use for 'how much did I spend on X', 'spending on X by store', comparing where they buy X, or plotting that spending on a map. Render with StoreComparisonBarChart (bars) or MerchantMapCard (map).",
      inputSchema: z.object({
        itemName: z.string().describe("Name of the item, e.g. 'peanuts'"),
      }),
      outputSchema: z.any(),
      tool: async ({ itemName }) => {
        try {
          const purchases = await fetchItemPurchases(itemName);
          if (purchases.length === 0) {
            return { success: false, itemName, stores: [], grandTotal: 0, message: `No recorded purchases matching "${itemName}".` };
          }
          const byStore = new Map<string, { storeName: string; address?: string; coordinates?: { lat: number; lng: number }; totalSpend: number; purchaseCount: number; units: number; lastPurchase: string }>();
          for (const p of purchases) {
            const entry = byStore.get(p.store) || { storeName: p.store, address: p.storeAddress, coordinates: p.storeCoordinates, totalSpend: 0, purchaseCount: 0, units: 0, lastPurchase: p.date };
            entry.totalSpend += p.unitPrice * p.quantity;
            entry.purchaseCount += 1;
            entry.units += p.quantity;
            if (p.date > entry.lastPurchase) entry.lastPurchase = p.date;
            byStore.set(p.store, entry);
          }
          const stores = Array.from(byStore.values())
            .map((s) => ({ ...s, totalSpend: Math.round(s.totalSpend * 100) / 100 }))
            .sort((a, b) => b.totalSpend - a.totalSpend);
          const grandTotal = Math.round(stores.reduce((sum, s) => sum + s.totalSpend, 0) * 100) / 100;
          return {
            success: true,
            itemName,
            stores,
            grandTotal,
            matchedProductNames: Array.from(new Set(purchases.map((p) => p.name))).slice(0, 10),
            message: `Spent ${currencySymbol}${grandTotal.toFixed(2)} on "${itemName}" across ${stores.length} store(s). Render StoreComparisonBarChart with data = stores mapped to { storeName, metric: totalSpend } and metricLabel 'Total spend'. If the user asked for a MAP, render MerchantMapCard instead with stores mapped to { name: storeName, address, coordinates, totalCost: totalSpend } (stores without coordinates can't be plotted — say how many). If matchedProductNames includes clearly unrelated products, mention it.`,
          };
        } catch (err) {
          console.error("[CartAssistant] get_item_spending_by_store failed:", err);
          return { success: false, itemName, stores: [], grandTotal: 0, message: "Could not load spending for this item." };
        }
      },
    }),

    defineTool({
      name: "get_spending_over_time",
      description: "Totals the user's shopping spend per month (or week) over a recent window, from their trip history. Use for spending trends, 'spending over time', or 'how much did I spend each month'. If no period is given, default to the last 12 months — do NOT ask the user for one. Render the result with SpendingOverTimeLineChart using data = buckets.",
      inputSchema: z.object({
        months: z.number().optional().describe("How many months back to include (default 12)"),
        groupBy: z.enum(["month", "week"]).optional().describe("Bucket size (default month)"),
      }),
      outputSchema: z.any(),
      tool: async ({ months = 12, groupBy = "month" }) => {
        try {
          const { supabase, userId } = getContext();
          const history = await getHistory(supabase, userId, 1000);
          const cutoff = new Date();
          cutoff.setMonth(cutoff.getMonth() - Math.max(1, Math.min(60, months)));

          const buckets = new Map<string, { start: Date; totalSpend: number; tripCount: number; categories: Map<string, number>; stores: Map<string, number> }>();
          for (const trip of history) {
            const date = new Date(trip.date);
            if (Number.isNaN(date.getTime()) || date < cutoff) continue;
            const start = groupBy === "week"
              ? new Date(date.getFullYear(), date.getMonth(), date.getDate() - date.getDay())
              : new Date(date.getFullYear(), date.getMonth(), 1);
            const key = start.toISOString();
            const bucket = buckets.get(key) || { start, totalSpend: 0, tripCount: 0, categories: new Map(), stores: new Map() };
            bucket.totalSpend += Number(trip.totalCost) || 0;
            bucket.tripCount += 1;
            const storeName = trip.storeInfo?.name || t('common.unknown_store');
            bucket.stores.set(storeName, (bucket.stores.get(storeName) || 0) + 1);
            for (const item of trip.items || []) {
              const cat = item.category || 'Other';
              bucket.categories.set(cat, (bucket.categories.get(cat) || 0) + (Number(item.price) || 0) * (Number(item.quantity) || 1));
            }
            buckets.set(key, bucket);
          }

          const top = (m: Map<string, number>) => [...m.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
          const data = [...buckets.values()]
            .sort((a, b) => a.start.getTime() - b.start.getTime())
            .map((b) => ({
              dateLabel: groupBy === "week"
                ? b.start.toLocaleDateString(language, { month: 'short', day: 'numeric' })
                : b.start.toLocaleDateString(language, { month: 'short', year: '2-digit' }),
              totalSpend: Math.round(b.totalSpend * 100) / 100,
              tripCount: b.tripCount,
              topCategory: top(b.categories),
              primaryStore: top(b.stores),
            }));
          const grandTotal = Math.round(data.reduce((sum, d) => sum + d.totalSpend, 0) * 100) / 100;
          if (data.length === 0) {
            return { success: false, buckets: [], grandTotal: 0, message: `No trips recorded in the last ${months} months.` };
          }
          return {
            success: true,
            buckets: data,
            grandTotal,
            message: `Total ${currencySymbol}${grandTotal.toFixed(2)} across ${data.length} ${groupBy}(s). Render SpendingOverTimeLineChart with data = buckets.`,
          };
        } catch (err) {
          console.error("[CartAssistant] get_spending_over_time failed:", err);
          return { success: false, buckets: [], grandTotal: 0, message: "Could not load spending history." };
        }
      },
    }),

    defineTool({
      name: "get_spending_by_category",
      description: "Totals the user's spending per category over a recent window, from their trip history (item price × quantity). Use for category breakdowns or 'what do I spend the most on'. If no period is given, default to the last 12 months — do NOT ask. Render the result with SpendingByCategoryPieChart using data = categories.",
      inputSchema: z.object({
        months: z.number().optional().describe("How many months back to include (default 12)"),
      }),
      outputSchema: z.any(),
      tool: async ({ months = 12 }) => {
        try {
          const { supabase, userId } = getContext();
          const history = await getHistory(supabase, userId, 1000);
          const cutoff = new Date();
          cutoff.setMonth(cutoff.getMonth() - Math.max(1, Math.min(60, months)));
          const totals = new Map<string, number>();
          for (const trip of history) {
            const date = new Date(trip.date);
            if (Number.isNaN(date.getTime()) || date < cutoff) continue;
            for (const item of trip.items || []) {
              const cat = item.category || 'Other';
              totals.set(cat, (totals.get(cat) || 0) + (Number(item.price) || 0) * (Number(item.quantity) || 1));
            }
          }
          const { useStore } = await import("../store");
          const userCategories = useStore.getState().categories || [];
          const categories = [...totals.entries()]
            .filter(([, amount]) => amount > 0)
            .sort((a, b) => b[1] - a[1])
            .map(([categoryName, amount]) => ({
              categoryName,
              amount: Math.round(amount * 100) / 100,
              color: userCategories.find((c: any) => c.name === categoryName)?.color,
            }));
          const total = Math.round(categories.reduce((sum, c) => sum + c.amount, 0) * 100) / 100;
          if (categories.length === 0) {
            return { success: false, categories: [], total: 0, message: `No purchases recorded in the last ${months} months.` };
          }
          return {
            success: true,
            categories,
            total,
            message: `Total ${currencySymbol}${total.toFixed(2)} across ${categories.length} categories. Render SpendingByCategoryPieChart with data = categories.`,
          };
        } catch (err) {
          console.error("[CartAssistant] get_spending_by_category failed:", err);
          return { success: false, categories: [], total: 0, message: "Could not load category spending." };
        }
      },
    }),

    defineTool({
      name: "search_for_store",
      description: "Searches for a store by name and optional location.",
      inputSchema: z.object({
        query: z.string().describe("Search query (store name)"),
        lat: z.number().optional().describe("Current latitude"),
        lng: z.number().optional().describe("Current longitude"),
      }),
      outputSchema: z.any(),
      tool: async ({ query, lat, lng }) => {
        try {
          return await searchStores(query, lat, lng);
        } catch (err) {
          console.error("[CartAssistant] search_for_store failed:", err);
          return [];
        }
      },
    }),

    defineTool({
      name: "compare_stores",
      description: "Compares pricing for common items across two specific stores.",
      inputSchema: z.object({
        storeA: z.string().describe("Name of the first store"),
        storeB: z.string().describe("Name of the second store"),
      }),
      outputSchema: z.any(),
      tool: async ({ storeA, storeB }) => {
        try {
          const { supabase, userId } = getContext();
          const analytics = await fetchSpendingAnalytics(supabase, userId);
          const itemData = analytics?.item_data || {};
          const getMatchingStoreNames = (input: string) => {
            const normalizedInput = input.toLowerCase().trim();
            const allStores = new Set<string>();
            Object.values(itemData).forEach((data: any) => {
              const storesMap = data.stores || data;
              Object.keys(storesMap).forEach(s => {
                if (s.toLowerCase().includes(normalizedInput)) {
                  allStores.add(s);
                }
              });
            });
            return Array.from(allStores);
          };
          const matchesA = getMatchingStoreNames(storeA);
          const matchesB = getMatchingStoreNames(storeB);
          if (!matchesA.length || !matchesB.length) {
            return { error: t('comparison.no_inventory_found', { store: !matchesA.length ? storeA : storeB }) };
          }
          const getAggregatedProducts = (matchingNames: string[]) => {
            const products: Array<{ name: string, category: string, price: number, date: string, canonicalId?: string, size?: number | string | null, unit?: string | null }> = [];
            Object.entries(itemData).forEach(([slug, data]: [string, any]) => {
              const storesMap = data.stores || data;
              let latestPriceObj: any = null;
              matchingNames.forEach(storeName => {
                const storeHistory = storesMap[storeName];
                if (storeHistory && storeHistory.length > 0) {
                  const latest = [...storeHistory].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
                  if (!latestPriceObj || new Date(latest.date) > new Date(latestPriceObj.date)) {
                    latestPriceObj = latest;
                  }
                }
              });
              if (latestPriceObj) {
                const itemEntry = itemData[slug];
                products.push({ 
                  name: itemEntry?.primary_name || slug, 
                  category: latestPriceObj.category || itemEntry?.category || 'Other', 
                  price: latestPriceObj.price, 
                  date: latestPriceObj.date,
                  canonicalId: slug,
                  size: latestPriceObj.size ?? itemEntry?.size ?? null,
                  unit: latestPriceObj.unit ?? itemEntry?.unit ?? null,
                });
              }
            });
            return products;
          };
          const productsA = getAggregatedProducts(matchesA);
          const productsB = getAggregatedProducts(matchesB);
          const resolvedA = matchesA.length === 1 ? matchesA[0] : storeA;
          const resolvedB = matchesB.length === 1 ? matchesB[0] : storeB;
          // Matching and the winner (on size-adjusted prices) come from the server.
          const { matches, summary } = await runMerchantBattle(resolvedA, resolvedB, productsA, productsB, language);
          return {
            storeA: resolvedA,
            storeB: resolvedB,
            storeAAddresses: battleStoreStreets(itemData, matchesA),
            storeBAddresses: battleStoreStreets(itemData, matchesB),
            winner: summary.winner,
            savings: summary.savings,
            totalA: summary.totalA,
            totalB: summary.totalB,
            sizeAdjusted: summary.sizeAdjusted,
            excludedCount: summary.excludedCount,
            itemCount: summary.itemCount,
            commonItems: matches,
          };
        } catch (err) {
          console.error("[CartAssistant] compare_stores failed:", err);
          return { error: "Failed to compare stores." };
        }
      }
    }),

    defineTool({
      name: "optimize_basket",
      description: "Runs 'Basket IQ' simulation on the active shopping basket, blending personal purchase history, the Geospatial Lattice, and (only for items with no other price coverage) a live Live Market Discovery lookup. For the richest results, call get_user_location first and pass lat/lng/address here.",
      inputSchema: z.object({
        lat: z.number().optional().describe("User's real-time latitude from get_user_location — enables live local-market lookups for basket items with no known price yet"),
        lng: z.number().optional().describe("User's real-time longitude from get_user_location"),
        address: z.string().optional().describe("Human-readable address from get_user_location"),
      }),
      outputSchema: z.any(),
      tool: async ({ lat, lng, address }) => {
        try {
          const { supabase, userId } = getContext();
          const { useStore } = await import("../store");
          const state = useStore.getState();
          const currentItems = state.operatingContext === 'personnel' ? state.latticeItems : state.items;

          if (currentItems.length === 0) return { error: t('basket.basket_empty_chat') };
          let isAuthorizedForSplit = isUltra;
          let creditSpent = false;
          if (!isAuthorizedForSplit && isPro) {
            const { data: spent } = await supabase.rpc('spend_credits', { p_user_id: userId, p_amount: 1 });
            if (spent) {
              isAuthorizedForSplit = true;
              creditSpent = true;
            }
          }
          const analytics = await fetchSpendingAnalytics(supabase, userId);
          const itemData = analytics?.item_data || {};

          const profile = await getProfile(supabase, userId);
          const radius = profile?.settings?.intelligence?.lattice_radius_miles || (isUltra ? CALIBRATION_DEFAULTS.latticeRadiusMiles : 3);
          const horizon = profile?.settings?.intelligence?.lattice_horizon_hours || CALIBRATION_DEFAULTS.latticeHorizonHours;

          // Lattice batch call — same multi-store richness search_global_lattice already gets,
          // this tool previously had none (it only read a single collapsed betterDeal field).
          const itemSlugs = currentItems.map(i => i.canonicalId || i.name);
          const latticeBatch = itemSlugs.length > 0
            ? await getBatchGlobalLatticePrices(supabase, itemSlugs, lat, lng, radius, horizon)
            : [];
          const latticeBatchByKey: Record<string, any[]> = {};
          (latticeBatch || []).forEach((row: any) => {
            (latticeBatchByKey[row.item_slug] ||= []).push(row);
          });

          // Cached groundedDeals (Tier 1 + Tier 2/3 blend) already on each item — free, no new call.
          const groundedDealsByKey: Record<string, any[] | undefined> = {};
          currentItems.forEach(item => {
            const cached = (item.metadata as any)?.groundedDeals;
            if (cached && cached.length > 0) groundedDealsByKey[item.canonicalId || item.name] = cached;
          });

          // Items with zero coverage across history + Lattice batch + cached deals.
          const uncovered = currentItems.filter(item => {
            const itemKey = item.canonicalId || item.name;
            const itemEntry = itemData[itemKey];
            const historyStores = itemEntry?.stores || itemEntry;
            const hasHistory = historyStores && Object.keys(historyStores).length > 0;
            const hasLattice = (latticeBatchByKey[itemKey]?.length || 0) > 0;
            const hasCached = (groundedDealsByKey[itemKey]?.length || 0) > 0;
            return !hasHistory && !hasLattice && !hasCached;
          });

          if (uncovered.length > 0 && lat != null && lng != null && isAuthorizedForSplit) {
            await resolveUncoveredItemsLive({
              supabase,
              items: uncovered.map(item => ({
                itemKey: item.canonicalId || item.name,
                queryName: item.name,
                price: item.price,
                size: item.size,
                unit: item.unit,
              })),
              userLat: lat,
              userLng: lng,
              userAddress: address,
              radiusMiles: radius,
              lookbackHours: horizon,
              maxItems: CHAT_LIVE_DISCOVERY_CAP,
              delayMs: 0, // interactive chat call — mirrors searchGrounding's skipDelay=true precedent
              onItemResolved: (itemKey, deals) => {
                if (deals.length === 0) return;
                groundedDealsByKey[itemKey] = deals;
                const item = currentItems.find(i => (i.canonicalId || i.name) === itemKey);
                if (item) {
                  state.updateItem(item.id, {
                    metadata: { ...(item.metadata || {}), groundedDeals: deals, groundingStatus: 'success' }
                  });
                }
              }
            });
          }

          const mergeInputs: MergeSourcesInput[] = currentItems.map(item => {
            const itemKey = item.canonicalId || item.name;
            const itemEntry = itemData[itemKey];
            return {
              itemKey,
              historyStores: itemEntry?.stores || itemEntry,
              latticeBatchRows: latticeBatchByKey[itemKey],
              groundedDeals: groundedDealsByKey[itemKey] as any,
            };
          });

          const items: BasketOptimizationItem[] = currentItems.map(item => {
            const itemKey = item.canonicalId || item.name;
            return {
              itemKey,
              displayName: itemData[itemKey]?.primary_name || item.name,
              quantity: item.quantity,
              currentPrice: item.price,
            };
          });

          const valueOfTimePerHour = profile?.settings?.intelligence?.value_of_time_per_hour;
          const origin = lat != null && lng != null ? { lat, lng } : null;

          const priceMap = mergeStorePriceSources(mergeInputs);
          const { storeTotals, smartSplit } = computeStoreTotalsAndSplit(
            items,
            priceMap,
            state.currentStore?.name || t('common.local_store'),
            { origin, valueOfTimePerHour }
          );

          // Real driving-distance/time enrichment over the already-computed,
          // price-based smartSplit — text-only for chat (no map/geometry here;
          // ComparisonView.tsx's Basket IQ page is where the full route map
          // renders). Opt-in: only when the caller supplied real coordinates.
          let routeStats: { totalDistanceMiles?: number; totalDurationMinutes?: number; extraMinutesVsSingleStore?: number; tripCostDollars?: number } | null = null;
          if (isAuthorizedForSplit && smartSplit && origin) {
            const enriched = await enrichSmartSplitWithRoute(smartSplit, origin, storeTotals[0], { valueOfTimePerHour });
            if (enriched.route) {
              routeStats = {
                totalDistanceMiles: enriched.route.totalDistanceMiles,
                totalDurationMinutes: enriched.route.totalDurationMinutes,
                extraMinutesVsSingleStore: enriched.route.extraMinutesVsSingleStore,
                tripCostDollars: enriched.route.tripCostDollars ?? smartSplit.tripCostDollars,
              };
            } else if (smartSplit.tripCostDollars != null) {
              // Real Mapbox enrichment didn't resolve — still surface the
              // synchronous Haversine estimate rather than showing nothing.
              routeStats = { tripCostDollars: smartSplit.tripCostDollars };
            }
          }

          return {
            basketItemCount: currentItems.length,
            bestSingleStore: storeTotals[0] ? {
              storeName: storeTotals[0].storeName,
              total: storeTotals[0].total,
              matches: storeTotals[0].matches,
              coverage: storeTotals[0].coverage
            } : null,
            smartSplit: isAuthorizedForSplit && smartSplit ? {
              total: smartSplit.total,
              route: smartSplit.plan.map(p => ({ name: p.displayName, store: p.store, price: p.price, tier: p.tier })),
              routeStats
            } : null,
            otherOptions: storeTotals.slice(1, 4).map(s => ({ storeName: s.storeName, total: s.total, matches: s.matches, coverage: s.coverage })),
            creditSpent
          };
        } catch (err) {
          console.error("[CartAssistant] optimize_basket failed:", err);
          return { error: "Optimization simulation failed." };
        }
      }
    })
  ];
};
